import React, { useEffect, useMemo, useRef, useState
} from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  fetchBalanceSheet,
  fetchHistoricalPrices,
  fetchIncomeStatement,
  fetchKeyMetrics,
  fetchStockQuote,
  searchStocks,
  type HistoricalPrice,
  type StockQuote,
  type StockSearchResult,
} from '../services/stockApi';
import { config } from '../config/config';
import stocksList from '../data/stocksList.json';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const digitalGiftExpandIcon = '/images/digitalgift-video-expand.svg';
const digitalGiftFloatingIcon = '/images/digitalgift-video-floating.svg';
const digitalGiftSearchIcon = '/images/digitalgift-search.svg';

type RangeKey = 'YTD' | '1Y' | '3Y' | '5Y' | '10Y' | 'ALL';

type StockListItem = {
  symbol: string;
  name: string;
  exchange: string;
};

type SearchQuotaStatus = {
  limit: number;
  used: number;
  remaining: number;
  isBlocked: boolean;
  status: 'new' | 'active' | 'blocked';
};

type DeviceSignals = {
  fingerprintJsId: string;
  canvasHash: string;
  webglHash: string;
  audioHash: string;
  fontsHash: string;
  hardwareHash: string;
  browserOsHash: string;
  timezone: string;
  language: string;
};

type CookieConsentChoice = 'accepted' | 'rejected';

const FREE_SEARCH_LIMIT = 30;
const QUOTA_REMINDER_MILESTONES = [15, 10, 5];
const VISITOR_ID_STORAGE_KEY = 'digitalgift_visitor_id_v1';
const DEVICE_SIGNALS_CACHE_KEY = 'digitalgift_device_signals_v1';
const COOKIE_CONSENT_STORAGE_KEY = 'digitalgift_cookie_consent_v1';
const ACCESSIBILITY_PREFS_STORAGE_KEY = 'digitalgift_accessibility_prefs_v1';
const DEVICE_SIGNALS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_VIDEO_URL = 'https://www.youtube.com/watch?v=q6fYEfiLlus';
const WORKSHOP_CTA_WINDOWS = [
  { start: 9 * 60 + 30, end: 10 * 60 + 30 },
  { start: 18 * 60 + 30, end: Number.POSITIVE_INFINITY },
] as const;
const WORKSHOP_CTA_MARKER_TIMES = [9 * 60 + 30, 18 * 60 + 30] as const;

type YouTubePlayerApi = {
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  playVideo: () => void;
  pauseVideo: () => void;
  destroy: () => void;
};

type YouTubePlayerOptions = {
  events: {
    onReady: (event: { target: YouTubePlayerApi }) => void;
    onStateChange: (event: { target: YouTubePlayerApi; data: number }) => void;
  };
};

declare global {
  interface Window {
    YT?: {
      Player: new (iframe: HTMLIFrameElement, options: YouTubePlayerOptions) => YouTubePlayerApi;
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

let youtubeIFrameApiPromise: Promise<void> | null = null;

const loadYouTubeIFrameApi = (): Promise<void> => {
  if (window.YT?.Player) return Promise.resolve();
  if (youtubeIFrameApiPromise) return youtubeIFrameApiPromise;

  youtubeIFrameApiPromise = new Promise<void>((resolve, reject) => {
    const previousReadyCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousReadyCallback?.();
      resolve();
    };

    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    script.onerror = () => {
      window.onYouTubeIframeAPIReady = previousReadyCallback;
      youtubeIFrameApiPromise = null;
      reject(new Error('Failed to load the YouTube IFrame API.'));
    };
    document.head.appendChild(script);
  });

  return youtubeIFrameApiPromise;
};

const RANGE_ORDER: RangeKey[] = ['YTD', '1Y', '3Y', '5Y', '10Y', 'ALL'];
const DIGITAL_GIFT_SUPPORTED_EXCHANGES = new Set(['NASDAQ', 'NYSE']);

const rangeToMs: Record<Exclude<RangeKey, 'YTD' | 'ALL'>, number> = {
  '1Y': 365 * 24 * 60 * 60 * 1000,
  '3Y': 3 * 365 * 24 * 60 * 60 * 1000,
  '5Y': 5 * 365 * 24 * 60 * 60 * 1000,
  '10Y': 10 * 365 * 24 * 60 * 60 * 1000,
};

const asPct = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const asUsd = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return 'N/A';
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const safeCagr = (latest: number, base: number, years: number): number | null => {
  if (!Number.isFinite(latest) || !Number.isFinite(base) || !Number.isFinite(years)) return null;
  if (latest <= 0 || base <= 0 || years <= 0) return null;
  return (Math.pow(latest / base, 1 / years) - 1) * 100;
};

const normalizeRoicPct = (raw: number | null | undefined): number | null => {
  if (raw == null || !Number.isFinite(raw)) return null;
  if (Math.abs(raw) <= 1) return raw * 100;
  return raw;
};

const getPublicFmpApiKey = (): string => {
  try {
    const runtime = (window as any)?.__RUNTIME_CONFIG__?.REACT_APP_FMP_API_KEY;
    const env = (process as any)?.env?.REACT_APP_FMP_API_KEY;
    return String(runtime || env || '').trim();
  } catch {
    return '';
  }
};

const normalizeBackendBaseUrl = (): string => {
  const base = String(config.backend?.baseUrl || '').trim();
  return base.replace(/\/$/, '');
};

const readStorage = (key: string): string => {
  try {
    return window.localStorage?.getItem(key) || '';
  } catch {
    return '';
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    window.localStorage?.setItem(key, value);
  } catch {
    // Ignore storage failures in private mode or blocked storage.
  }
};

const removeStorage = (key: string): void => {
  try {
    window.localStorage?.removeItem(key);
  } catch {
    // Ignore storage failures in private mode or blocked storage.
  }
};

const getCookieConsentChoice = (): CookieConsentChoice | null => {
  const raw = readStorage(COOKIE_CONSENT_STORAGE_KEY).trim().toLowerCase();
  if (raw === 'accepted' || raw === 'rejected') return raw;
  return null;
};

const setCookieConsentChoice = (choice: CookieConsentChoice): void => {
  writeStorage(COOKIE_CONSENT_STORAGE_KEY, choice);
};

const ensureVisitorId = (): string => {
  const existing = readStorage(VISITOR_ID_STORAGE_KEY).trim();
  if (existing.length >= 10) return existing;

  const generated =
    typeof window.crypto?.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `guest-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

  writeStorage(VISITOR_ID_STORAGE_KEY, generated);
  return generated;
};

const fallbackQuotaStatus = (): SearchQuotaStatus => ({
  limit: FREE_SEARCH_LIMIT,
  used: 0,
  remaining: FREE_SEARCH_LIMIT,
  isBlocked: false,
  status: 'new',
});

const hashText = async (input: string): Promise<string> => {
  try {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(input);
    const digest = await window.crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return input;
  }
};

const buildCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 220;
    canvas.height = 42;
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.textBaseline = 'top';
    ctx.font = '16px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(0, 0, 220, 42);
    ctx.fillStyle = '#069';
    ctx.fillText('GARP fingerprint', 6, 6);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('1234567890', 6, 22);
    return canvas.toDataURL();
  } catch {
    return '';
  }
};

const buildWebGlFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (!gl) return '';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const vendor = debugInfo ? gl.getParameter((debugInfo as any).UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR);
    const renderer = debugInfo ? gl.getParameter((debugInfo as any).UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER);
    const maxTexture = gl.getParameter(gl.MAX_TEXTURE_SIZE);
    return `${vendor}|${renderer}|${maxTexture}`;
  } catch {
    return '';
  }
};

const buildFontsFingerprint = (): string => {
  try {
    const families = ['Arial', 'Times New Roman', 'Courier New', 'Verdana', 'Georgia', 'Tahoma', 'Trebuchet MS'];
    const testText = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const baseFonts = ['monospace', 'sans-serif', 'serif'];

    const span = document.createElement('span');
    span.style.position = 'absolute';
    span.style.left = '-9999px';
    span.style.fontSize = testSize;
    span.textContent = testText;
    document.body.appendChild(span);

    const baseWidths = baseFonts.map((f) => {
      span.style.fontFamily = f;
      return span.offsetWidth;
    });

    const detected = families.map((font) => {
      const widths = baseFonts.map((base, idx) => {
        span.style.fontFamily = `'${font}',${base}`;
        return span.offsetWidth !== baseWidths[idx] ? '1' : '0';
      });
      return `${font}:${widths.join('')}`;
    });

    document.body.removeChild(span);
    return detected.join('|');
  } catch {
    return '';
  }
};

const buildAudioFingerprint = async (): Promise<string> => {
  try {
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext | undefined;
    if (!Ctx) return '';
    const audioCtx = new Ctx();
    const osc = audioCtx.createOscillator();
    const analyser = audioCtx.createAnalyser();
    osc.type = 'triangle';
    osc.frequency.value = 10000;
    osc.connect(analyser);
    analyser.connect(audioCtx.destination);
    const data = new Float32Array(analyser.frequencyBinCount);
    osc.start(0);
    analyser.getFloatFrequencyData(data);
    osc.stop();
    void audioCtx.close();
    return Array.from(data.slice(0, 32)).join(',');
  } catch {
    return '';
  }
};

const buildDeviceSignals = async (allowFunctionalStorage: boolean): Promise<DeviceSignals> => {
  if (allowFunctionalStorage) {
    try {
      const raw = readStorage(DEVICE_SIGNALS_CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { at?: number; value?: DeviceSignals };
        if (parsed?.at && parsed?.value && Date.now() - parsed.at < DEVICE_SIGNALS_CACHE_TTL_MS) {
          return parsed.value;
        }
      }
    } catch {
      // Ignore cache parsing errors and regenerate.
    }
  }

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const lang = navigator.language || '';
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const cores = String(navigator.hardwareConcurrency || '');
  const memory = String((navigator as any).deviceMemory || '');
  const screenPart = `${window.screen?.width || ''}x${window.screen?.height || ''}x${window.screen?.colorDepth || ''}`;

  const canvasRaw = buildCanvasFingerprint();
  const webglRaw = buildWebGlFingerprint();
  const fontsRaw = buildFontsFingerprint();
  const audioRaw = await buildAudioFingerprint();

  const fpLikeRaw = `${ua}|${platform}|${lang}|${tz}|${screenPart}`;
  const hardwareRaw = `${cores}|${memory}|${screenPart}`;
  const browserOsRaw = `${ua}|${platform}|${navigator.vendor || ''}`;

  const computed: DeviceSignals = {
    fingerprintJsId: await hashText(fpLikeRaw),
    canvasHash: await hashText(canvasRaw),
    webglHash: await hashText(webglRaw),
    audioHash: await hashText(audioRaw),
    fontsHash: await hashText(fontsRaw),
    hardwareHash: await hashText(hardwareRaw),
    browserOsHash: await hashText(browserOsRaw),
    timezone: tz,
    language: lang,
  };

  if (allowFunctionalStorage) {
    writeStorage(DEVICE_SIGNALS_CACHE_KEY, JSON.stringify({ at: Date.now(), value: computed }));
  }

  return computed;
};

const parseQuotaStatus = (raw: any): SearchQuotaStatus => {
  const limit = Number.isFinite(raw?.limit) ? Number(raw.limit) : FREE_SEARCH_LIMIT;
  const usedRaw = Number.isFinite(raw?.used) ? Number(raw.used) : 0;
  const used = Math.max(0, Math.min(limit, usedRaw));
  const remaining = Math.max(0, limit - used);
  const isBlocked = Boolean(raw?.isBlocked) || used >= limit;
  const status: 'new' | 'active' | 'blocked' = isBlocked
    ? 'blocked'
    : used === 0
      ? 'new'
      : 'active';
  return { limit, used, remaining, isBlocked, status };
};

const parseQuotaError = async (response: Response): Promise<string> => {
  try {
    const json = await response.json();
    return String(json?.message || '').trim();
  } catch {
    return '';
  }
};

const toUserFriendlyError = (err: unknown, fallback: string): string => {
  const message = String((err as any)?.message || '').trim();
  if (!message) return fallback;
  const lower = message.toLowerCase();
  if (lower.includes('failed to fetch') || lower.includes('networkerror') || lower.includes('network request failed')) {
    return 'Search service is temporarily unavailable. Please try again in a moment.';
  }
  return message;
};

const hasFiscalSourceTag = (value: unknown): boolean => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return false;
  return raw.includes('fiscal');
};

const getSearchResultExchange = (row: StockSearchResult): string =>
  String(row.exchangeShortName || row.exchange || row.stockExchange || '').trim().toUpperCase();

const getYoutubeEmbedUrl = (value: string): string => {
  try {
    const url = new URL(value);
    let videoId = '';
    if (url.hostname === 'youtu.be') {
      videoId = url.pathname.slice(1);
    } else if (url.hostname.endsWith('youtube.com')) {
      videoId = url.searchParams.get('v') || url.pathname.split('/').filter(Boolean).pop() || '';
    }
    return videoId ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}` : '';
  } catch {
    return '';
  }
};

const fetchSearchQuotaStatus = async (backendBase: string, visitorId: string, deviceSignals: DeviceSignals): Promise<SearchQuotaStatus> => {
  const response = await fetch(`${backendBase}/public/stock-search/status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      visitorId,
      deviceSignals,
    }),
  });
  if (!response.ok) {
    const serverMessage = await parseQuotaError(response);
    throw new Error(serverMessage || `status_failed_${response.status}`);
  }
  const json = await response.json();
  return parseQuotaStatus(json);
};

const consumeSearchQuota = async (
  backendBase: string,
  visitorId: string,
  deviceSignals: DeviceSignals,
  query: string,
  symbol: string
): Promise<SearchQuotaStatus> => {
  const response = await fetch(`${backendBase}/public/stock-search/consume`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ visitorId, query, symbol, deviceSignals }),
  });
  if (!response.ok) {
    const serverMessage = await parseQuotaError(response);
    throw new Error(serverMessage || `consume_failed_${response.status}`);
  }
  const json = await response.json();
  return parseQuotaStatus(json);
};

const DigitalGiftScreen: React.FC = () => {
  const [query, setQuery] = useState('');
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [searchResults, setSearchResults] = useState<StockSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedRange, setSelectedRange] = useState<RangeKey>('10Y');
  const [quote, setQuote] = useState<StockQuote | null>(null);
  const [isQuoteLogoFailed, setIsQuoteLogoFailed] = useState(false);
  const [history, setHistory] = useState<HistoricalPrice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(DEFAULT_VIDEO_URL);
  const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(null);
  const [isYoutubeStarted, setIsYoutubeStarted] = useState(false);
  const [isVideoPaused, setIsVideoPaused] = useState(true);
  const [videoPlayback, setVideoPlayback] = useState({ currentTime: 0, duration: 0 });
  const [dismissedWorkshopCtaWindows, setDismissedWorkshopCtaWindows] = useState<Set<number>>(() => new Set());
  const [isPreviewMuted, setIsPreviewMuted] = useState(false);
  const [showPreviewMenu, setShowPreviewMenu] = useState(false);
  const [showVideoChapters, setShowVideoChapters] = useState(false);
  const [isVideoFloating, setIsVideoFloating] = useState(false);
  const [floatingVideoPosition, setFloatingVideoPosition] = useState({ left: 24, top: 24, width: 420, height: 236 });
  const [quotaStatus, setQuotaStatus] = useState<SearchQuotaStatus>(fallbackQuotaStatus());
  const [isQuotaLoading, setIsQuotaLoading] = useState(true);
  const [showQuotaReminder, setShowQuotaReminder] = useState(false);
  const [quotaReminderCount, setQuotaReminderCount] = useState<number | null>(null);
  const [quotaError, setQuotaError] = useState('');
  const [searchError, setSearchError] = useState('');
  const [cookieConsent, setCookieConsent] = useState<CookieConsentChoice | null>(null);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [hasFiscalDataSource, setHasFiscalDataSource] = useState(false);
  const [legalModalUrl, setLegalModalUrl] = useState('');
  const [legalModalTitle, setLegalModalTitle] = useState('');
  const [legalModalDescription, setLegalModalDescription] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showAccessibilityPanel, setShowAccessibilityPanel] = useState(false);
  const [a11yLargeText, setA11yLargeText] = useState(false);
  const [a11yHighContrast, setA11yHighContrast] = useState(false);
  const [a11yReadableFont, setA11yReadableFont] = useState(false);
  const [a11yReduceMotion, setA11yReduceMotion] = useState(false);
  const [a11yUnderlineLinks, setA11yUnderlineLinks] = useState(false);
  const [a11yHighlightHeadings, setA11yHighlightHeadings] = useState(false);
  const [a11yInvertColors, setA11yInvertColors] = useState(false);
  const [a11yGrayscale, setA11yGrayscale] = useState(false);
  const [a11ySepia, setA11ySepia] = useState(false);
  const [a11yLargeCursor, setA11yLargeCursor] = useState(false);
  const [a11yHideImages, setA11yHideImages] = useState(false);
  const [a11yMarkImages, setA11yMarkImages] = useState(false);
  const [a11yStopAnimations, setA11yStopAnimations] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const youtubeIframeRef = useRef<HTMLIFrameElement | null>(null);
  const youtubePlayerRef = useRef<YouTubePlayerApi | null>(null);
  const playerPreviewRef = useRef<HTMLDivElement | null>(null);
  const playerSlotRef = useRef<HTMLDivElement | null>(null);
  const floatingVideoDragRef = useRef<{ pointerX: number; pointerY: number; left: number; top: number } | null>(null);
  const legalModalPanelRef = useRef<HTMLDivElement | null>(null);
  const legalModalCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const quotaReminderCloseButtonRef = useRef<HTMLButtonElement | null>(null);
  const scheduledQuotaRemindersRef = useRef<Set<number>>(new Set());
  const quotaReminderTimeoutsRef = useRef<number[]>([]);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const accessibilityPanelRef = useRef<HTMLDivElement | null>(null);
  const accessibilityButtonRef = useRef<HTMLButtonElement | null>(null);
  const searchWrapRef = useRef<HTMLDivElement | null>(null);
  const skipSearchOnceRef = useRef(false);
  const backendBase = useMemo(() => normalizeBackendBaseUrl(), []);
  const visitorIdRef = useRef('');
  const deviceSignalsRef = useRef<DeviceSignals | null>(null);
  const publicApiKey = useMemo(() => getPublicFmpApiKey(), []);
  const allowFunctionalStorage = cookieConsent === 'accepted';

  const openQuotaReminder = (remaining: number) => {
    setQuotaReminderCount(remaining);
    setShowQuotaReminder(true);
  };

  const scheduleQuotaReminder = (status: SearchQuotaStatus) => {
    const remaining = status.remaining;
    if (status.isBlocked || !QUOTA_REMINDER_MILESTONES.includes(remaining) || scheduledQuotaRemindersRef.current.has(remaining)) {
      return;
    }

    scheduledQuotaRemindersRef.current.add(remaining);
    const timeoutId = window.setTimeout(() => {
      quotaReminderTimeoutsRef.current = quotaReminderTimeoutsRef.current.filter((id) => id !== timeoutId);
      openQuotaReminder(remaining);
    }, 900);
    quotaReminderTimeoutsRef.current.push(timeoutId);
  };

  useEffect(() => () => {
    quotaReminderTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
  }, []);

  useEffect(() => {
    if (!showQuotaReminder) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowQuotaReminder(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    quotaReminderCloseButtonRef.current?.focus();
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showQuotaReminder]);

  useEffect(() => {
    if (!isVideoFloating) return;

    const onPointerMove = (event: PointerEvent) => {
      const drag = floatingVideoDragRef.current;
      if (!drag) return;
      const playerBounds = playerPreviewRef.current?.getBoundingClientRect();
      const playerWidth = playerBounds?.width ?? floatingVideoPosition.width;
      const playerHeight = playerBounds?.height ?? floatingVideoPosition.height;
      setFloatingVideoPosition((current) => ({
        ...current,
        left: Math.min(
          Math.max(0, drag.left + event.clientX - drag.pointerX),
          Math.max(0, window.innerWidth - playerWidth - 12)
        ),
        top: Math.min(
          Math.max(0, drag.top + event.clientY - drag.pointerY),
          Math.max(0, window.innerHeight - playerHeight - 12)
        ),
      }));
    };
    const onPointerUp = () => {
      floatingVideoDragRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [isVideoFloating, floatingVideoPosition.width, floatingVideoPosition.height]);

  const beginFloatingVideoDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    floatingVideoDragRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      left: floatingVideoPosition.left,
      top: floatingVideoPosition.top,
    };
  };

  const expandVideo = async () => {
    const player = playerPreviewRef.current;
    if (!player) return;

    if (typeof player.requestFullscreen !== 'function') {
      setIsVideoFloating(true);
      return;
    }

    try {
      await player.requestFullscreen();
    } catch (error) {
      console.error('Could not expand the video to fullscreen.', error);
      setIsVideoFloating(true);
    }
  };

  const toggleVideoPlayback = () => {
    if (youtubeEmbedSrc) {
      if (!isYoutubeStarted) {
        setIsYoutubeStarted(true);
        setIsVideoPaused(false);
        return;
      }

      const player = youtubePlayerRef.current;
      if (!player) return;
      if (player.getPlayerState() === 1) {
        player.pauseVideo();
        setIsVideoPaused(true);
      } else {
        player.playVideo();
        setIsVideoPaused(false);
      }
      return;
    }

    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch((error: unknown) => {
        console.error('Could not resume video playback.', error);
      });
    } else {
      video.pause();
    }
  };

  useEffect(() => {
    const existing = getCookieConsentChoice();
    setCookieConsent(existing);
    setShowCookieBanner(existing == null);
  }, []);

  useEffect(() => {
    const documentElement = document.documentElement;
    const previousLang = documentElement.lang;
    const previousDir = documentElement.dir;
    documentElement.lang = 'en';
    documentElement.dir = 'ltr';
    return () => {
      documentElement.lang = previousLang;
      documentElement.dir = previousDir;
    };
  }, []);

  useEffect(() => {
    try {
      const raw = readStorage(ACCESSIBILITY_PREFS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        largeText?: boolean;
        highContrast?: boolean;
        readableFont?: boolean;
        reduceMotion?: boolean;
        underlineLinks?: boolean;
        highlightHeadings?: boolean;
        invertColors?: boolean;
        grayscale?: boolean;
        sepia?: boolean;
        largeCursor?: boolean;
        hideImages?: boolean;
        markImages?: boolean;
        stopAnimations?: boolean;
      };
      setA11yLargeText(Boolean(parsed?.largeText));
      setA11yHighContrast(Boolean(parsed?.highContrast));
      setA11yReadableFont(Boolean(parsed?.readableFont));
      setA11yReduceMotion(Boolean(parsed?.reduceMotion));
      setA11yUnderlineLinks(Boolean(parsed?.underlineLinks));
      setA11yHighlightHeadings(Boolean(parsed?.highlightHeadings));
      setA11yInvertColors(Boolean(parsed?.invertColors));
      setA11yGrayscale(Boolean(parsed?.grayscale));
      setA11ySepia(Boolean(parsed?.sepia));
      setA11yLargeCursor(Boolean(parsed?.largeCursor));
      setA11yHideImages(Boolean(parsed?.hideImages));
      setA11yMarkImages(Boolean(parsed?.markImages));
      setA11yStopAnimations(Boolean(parsed?.stopAnimations));
    } catch {
      // Ignore malformed stored accessibility preferences.
    }
  }, []);

  useEffect(() => {
    writeStorage(
      ACCESSIBILITY_PREFS_STORAGE_KEY,
      JSON.stringify({
        largeText: a11yLargeText,
        highContrast: a11yHighContrast,
        readableFont: a11yReadableFont,
        reduceMotion: a11yReduceMotion,
        underlineLinks: a11yUnderlineLinks,
        highlightHeadings: a11yHighlightHeadings,
        invertColors: a11yInvertColors,
        grayscale: a11yGrayscale,
        sepia: a11ySepia,
        largeCursor: a11yLargeCursor,
        hideImages: a11yHideImages,
        markImages: a11yMarkImages,
        stopAnimations: a11yStopAnimations,
      })
    );
  }, [a11yGrayscale, a11yHighContrast, a11yHideImages, a11yHighlightHeadings, a11yInvertColors, a11yLargeCursor, a11yLargeText, a11yMarkImages, a11yReadableFont, a11yReduceMotion, a11ySepia, a11yStopAnimations, a11yUnderlineLinks]);

  useEffect(() => {
    if (!showAccessibilityPanel) return;

    const onPointerDown = (event: MouseEvent) => {
      const panel = accessibilityPanelRef.current;
      const launcher = accessibilityButtonRef.current;
      const target = event.target as Node | null;
      if (!target) return;
      if (panel?.contains(target)) return;
      if (launcher?.contains(target)) return;
      setShowAccessibilityPanel(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setShowAccessibilityPanel(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showAccessibilityPanel]);

  const applyCookieChoice = (choice: CookieConsentChoice) => {
    setCookieConsentChoice(choice);
    setCookieConsent(choice);
    setShowCookieBanner(false);
    deviceSignalsRef.current = null;
    if (choice === 'rejected') {
      removeStorage(DEVICE_SIGNALS_CACHE_KEY);
    }
  };

  const localStocks = useMemo(() => {
    const rows = (stocksList as StockListItem[]) || [];
    const seen = new Set<string>();
    const out: StockListItem[] = [];
    for (const row of rows) {
      const symbol = String(row?.symbol || '').trim().toUpperCase();
      if (!symbol || seen.has(symbol)) continue;
      seen.add(symbol);
      out.push({
        symbol,
        name: String(row?.name || '').trim(),
        exchange: String(row?.exchange || '').trim(),
      });
    }
    return out;
  }, []);

  const hasFiscalCoverageForSelectedSymbol = useMemo(() => {
    const symbol = String(selectedSymbol || '').trim().toUpperCase();
    if (!symbol) return false;
    const found = localStocks.find((row) => String(row.symbol || '').trim().toUpperCase() === symbol);
    if (!found) return false;
    return DIGITAL_GIFT_SUPPORTED_EXCHANGES.has(String(found.exchange || '').trim().toUpperCase());
  }, [localStocks, selectedSymbol]);

  const effectiveVideoSrc = useMemo(() => {
    const trimmed = videoUrl.trim();
    if (trimmed) return trimmed;
    return videoObjectUrl ?? '';
  }, [videoObjectUrl, videoUrl]);
  const youtubeEmbedSrc = useMemo(() => getYoutubeEmbedUrl(videoUrl.trim()), [videoUrl]);

  useEffect(() => {
    setIsYoutubeStarted(false);
    setIsVideoPaused(true);
    setVideoPlayback({ currentTime: 0, duration: 0 });
    setDismissedWorkshopCtaWindows(new Set());
  }, [effectiveVideoSrc, youtubeEmbedSrc]);

  useEffect(() => {
    if (!youtubeEmbedSrc || !isYoutubeStarted) return;

    let player: YouTubePlayerApi | null = null;
    let playbackPollId: number | null = null;
    let cancelled = false;
    const updatePlayback = (activePlayer: YouTubePlayerApi) => {
      const currentTime = Math.max(0, activePlayer.getCurrentTime());
      const duration = Math.max(0, activePlayer.getDuration());
      setVideoPlayback((previous) => (
        Math.floor(previous.currentTime) === Math.floor(currentTime)
        && Math.floor(previous.duration) === Math.floor(duration)
          ? previous
          : { currentTime, duration }
      ));
    };

    void loadYouTubeIFrameApi()
      .then(() => {
        if (cancelled || !window.YT || !youtubeIframeRef.current) return;
        player = new window.YT.Player(youtubeIframeRef.current, {
          events: {
            onReady: (event) => {
              player = event.target;
              youtubePlayerRef.current = event.target;
              updatePlayback(event.target);
              playbackPollId = window.setInterval(() => {
                if (player) updatePlayback(player);
              }, 500);
            },
            onStateChange: (event) => {
              player = event.target;
              youtubePlayerRef.current = event.target;
              if (event.data === 1) setIsVideoPaused(false);
              if (event.data === 0 || event.data === 2) setIsVideoPaused(true);
              updatePlayback(event.target);
            },
          },
        });
      })
      .catch((error: unknown) => {
        console.error('Workshop video timing is unavailable because the YouTube player API failed to load.', error);
      });

    return () => {
      cancelled = true;
      if (playbackPollId !== null) window.clearInterval(playbackPollId);
      if (youtubePlayerRef.current === player) youtubePlayerRef.current = null;
      player?.destroy();
    };
  }, [isYoutubeStarted, youtubeEmbedSrc]);

  useEffect(() => {
    setIsQuoteLogoFailed(false);
  }, [selectedSymbol]);

  useEffect(() => {
    visitorIdRef.current = ensureVisitorId();
    if (!backendBase) {
      setQuotaError('Search quota service is currently unavailable.');
      setIsQuotaLoading(false);
      return;
    }

    let cancelled = false;
    const loadQuota = async () => {
      setIsQuotaLoading(true);
      setQuotaError('');
      try {
        deviceSignalsRef.current = await buildDeviceSignals(allowFunctionalStorage);
        const status = await fetchSearchQuotaStatus(backendBase, visitorIdRef.current, deviceSignalsRef.current);
        if (cancelled) return;
        setQuotaStatus(status);
        scheduleQuotaReminder(status);
      } catch (err: any) {
        if (cancelled) return;
        setQuotaError(toUserFriendlyError(err, 'Search quota service is currently unavailable.'));
      } finally {
        if (!cancelled) setIsQuotaLoading(false);
      }
    };

    void loadQuota();
    return () => {
      cancelled = true;
    };
  }, [allowFunctionalStorage, backendBase]);

  useEffect(() => {
    return () => {
      if (videoObjectUrl) {
        URL.revokeObjectURL(videoObjectUrl);
      }
    };
  }, [videoObjectUrl]);

  useEffect(() => {
    let cancelled = false;
    const normalized = query.trim().toUpperCase();

    if (quotaStatus.isBlocked) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    if (skipSearchOnceRef.current) {
      skipSearchOnceRef.current = false;
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    if (normalized.length < 1) {
      setSearchResults([]);
      return;
    }

    const t = window.setTimeout(async () => {
      setIsSearching(true);
      try {
        let remoteRows: StockSearchResult[] = [];
        try {
          remoteRows = await searchStocks(normalized, 50, publicApiKey);
        } catch {
          remoteRows = [];
        }

        const localRows: StockSearchResult[] = localStocks
          .filter((row) => {
            const sym = row.symbol.toUpperCase();
            const name = row.name.toUpperCase();
            return sym.includes(normalized) || name.includes(normalized);
          })
          .map((row) => ({
            symbol: row.symbol,
            name: row.name,
            currency: 'USD',
            stockExchange: row.exchange,
            exchangeShortName: row.exchange,
            exchange: row.exchange,
          }));

        const merged = [...(remoteRows || []), ...localRows]
          .filter((item) => DIGITAL_GIFT_SUPPORTED_EXCHANGES.has(getSearchResultExchange(item)));
        const unique = new Map<string, StockSearchResult>();
        for (const item of merged) {
          const symbol = String(item?.symbol || '').trim().toUpperCase();
          if (!symbol || unique.has(symbol)) continue;
          unique.set(symbol, item);
        }

        const ranked = Array.from(unique.values())
          .sort((a, b) => {
            const as = String(a.symbol || '').toUpperCase();
            const bs = String(b.symbol || '').toUpperCase();
            const an = String(a.name || '').toUpperCase();
            const bn = String(b.name || '').toUpperCase();

            const aStarts = as.startsWith(normalized) ? 0 : an.startsWith(normalized) ? 1 : 2;
            const bStarts = bs.startsWith(normalized) ? 0 : bn.startsWith(normalized) ? 1 : 2;
            if (aStarts !== bStarts) return aStarts - bStarts;

            return as.localeCompare(bs);
          })
          .slice(0, 8);

        if (!cancelled) setSearchResults(ranked);
      } catch {
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [localStocks, publicApiKey, query, quotaStatus.isBlocked]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const root = searchWrapRef.current;
      if (!root) return;
      const target = event.target as Node | null;
      if (target && !root.contains(target)) {
        setSearchResults([]);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!selectedSymbol) {
        setQuote(null);
        setHistory([]);
        setIsLoading(false);
        setHasFiscalDataSource(false);
        return;
      }

      setIsLoading(true);
      try {
        const [q, h] = await Promise.all([
          fetchStockQuote(selectedSymbol, publicApiKey),
          fetchHistoricalPrices(selectedSymbol, publicApiKey),
        ]);
        if (cancelled) return;
        setQuote(q);
        setHistory(Array.isArray(h) ? h : []);
        const fromQuote = hasFiscalSourceTag((q as any)?.source) || hasFiscalSourceTag((q as any)?.provider);
        const fromHistory = (Array.isArray(h) ? h : []).some((row: any) => hasFiscalSourceTag(row?.source) || hasFiscalSourceTag(row?.provider));
        setHasFiscalDataSource(fromQuote || fromHistory);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [publicApiKey, selectedSymbol]);

  const filteredHistory = useMemo(() => {
    if (history.length === 0) return [] as HistoricalPrice[];
    if (selectedRange === 'ALL') return history;

    const lastDate = new Date(history[history.length - 1].date);
    if (!Number.isFinite(lastDate.getTime())) return history;

    if (selectedRange === 'YTD') {
      const ytdStart = new Date(lastDate.getFullYear(), 0, 1).getTime();
      return history.filter((p) => new Date(p.date).getTime() >= ytdStart);
    }

    const cutoff = lastDate.getTime() - rangeToMs[selectedRange as Exclude<RangeKey, 'YTD' | 'ALL'>];
    return history.filter((p) => new Date(p.date).getTime() >= cutoff);
  }, [history, selectedRange]);

  const topStats = useMemo(() => {
    if (filteredHistory.length < 2) {
      return { rangeReturn: null as number | null, aboveLow: null as number | null, belowHigh: null as number | null };
    }
    const first = filteredHistory[0].close;
    const closes = filteredHistory.map((p) => p.close).filter((x) => Number.isFinite(x));
    const low = closes.length ? Math.min(...closes) : null;
    const high = closes.length ? Math.max(...closes) : null;
    const current = quote?.price ?? filteredHistory[filteredHistory.length - 1].close;

    const rangeReturn = first > 0 ? ((current - first) / first) * 100 : null;
    const aboveLow = low != null && low > 0 ? ((current - low) / low) * 100 : null;
    const belowHigh = high != null && high > 0 ? ((current - high) / high) * 100 : null;

    return { rangeReturn, aboveLow, belowHigh };
  }, [filteredHistory, quote?.price]);

  const metricRows = useMemo(() => {
    return {
      revenueGrowth5y: 'N/A',
      earningsGrowth5y: 'N/A',
      netProfitMargin: 'N/A',
      roic: 'N/A',
      cashToDebt: 'N/A',
      yearsOfProfitability: 'N/A',
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadMetrics = async () => {
      if (!selectedSymbol) {
        setMetricState({
          revenueGrowth5y: 'N/A',
          earningsGrowth5y: 'N/A',
          netProfitMargin: 'N/A',
          roic: 'N/A',
          cashToDebt: 'N/A',
          yearsOfProfitability: 'N/A',
        });
        return;
      }

      try {
        const [incomeResult, keyMetricsResult, balanceResult] = await Promise.allSettled([
          fetchIncomeStatement(selectedSymbol, 'annual', 10, publicApiKey),
          fetchKeyMetrics(selectedSymbol, 'annual', 10, publicApiKey),
          fetchBalanceSheet(selectedSymbol, 'annual', 2, publicApiKey),
        ]);
        if (cancelled) return;

        const income = incomeResult.status === 'fulfilled' ? incomeResult.value : [];
        const keyMetrics = keyMetricsResult.status === 'fulfilled' ? keyMetricsResult.value : [];
        const balance = balanceResult.status === 'fulfilled' ? balanceResult.value : [];
        const incomeHasFiscal = (income || []).some((row: any) => hasFiscalSourceTag(row?.source) || hasFiscalSourceTag(row?.provider));
        const keyMetricsHasFiscal = (keyMetrics || []).some((row: any) => hasFiscalSourceTag(row?.source) || hasFiscalSourceTag(row?.provider));
        const balanceHasFiscal = (balance || []).some((row: any) => hasFiscalSourceTag(row?.source) || hasFiscalSourceTag(row?.provider));
        if (incomeHasFiscal || keyMetricsHasFiscal || balanceHasFiscal) {
          setHasFiscalDataSource(true);
        }

        const incomeAnnual = (income || []).slice().sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        const latestIncome = incomeAnnual.length ? incomeAnnual[incomeAnnual.length - 1] : null;
        const baseIncome5y = incomeAnnual.length >= 6 ? incomeAnnual[incomeAnnual.length - 6] : null;

        const revenueGrowth5y = latestIncome && baseIncome5y
          ? safeCagr(latestIncome.revenue, baseIncome5y.revenue, 5)
          : null;

        const earningsGrowth5y = latestIncome && baseIncome5y
          ? safeCagr(latestIncome.netIncome, baseIncome5y.netIncome, 5)
          : null;

        const netProfitMargin = latestIncome && latestIncome.revenue > 0
          ? (latestIncome.netIncome / latestIncome.revenue) * 100
          : null;

        const kmLatest = (keyMetrics || [])[0] || null;
        const roic = normalizeRoicPct(kmLatest?.roic ?? kmLatest?.returnOnInvestedCapital ?? null);

        const toFiniteNumber = (value: unknown): number | null => {
          if (value == null || value === '') return null;
          const parsed = typeof value === 'number' ? value : Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const firstPositiveNumber = (...values: unknown[]): number | null => {
          for (const value of values) {
            const parsed = toFiniteNumber(value);
            if (parsed != null && parsed > 0) return parsed;
          }
          return null;
        };

        const getCash = (row: any): number | null => firstPositiveNumber(
          row?.cashAndCashEquivalents,
          row?.cashAndShortTermInvestments,
          row?.cashAndCashEquivalentsAtCarryingValue,
        );

        const getDebt = (row: any): number | null => {
          const reportedDebt = firstPositiveNumber(row?.totalDebt);
          if (reportedDebt != null) return reportedDebt;

          return firstPositiveNumber(
            (toFiniteNumber(row?.shortTermDebt) ?? 0) + (toFiniteNumber(row?.longTermDebt) ?? 0),
            row?.totalDebtAndCapitalLeaseObligation,
          );
        };

        const balanceWithDebt = (balance || []).find((row: any) => {
          return getCash(row) != null && getDebt(row) != null;
        }) || null;
        const cash = balanceWithDebt ? getCash(balanceWithDebt) : null;
        const debt = balanceWithDebt ? getDebt(balanceWithDebt) : null;
        const cashToDebt = cash != null && debt != null && debt > 0 ? cash / debt : null;

        const positiveYears = incomeAnnual.filter((r) => Number.isFinite(r.netIncome) && r.netIncome > 0).length;

        if (cancelled) return;
        setMetricState({
          revenueGrowth5y: asPct(revenueGrowth5y),
          earningsGrowth5y: asPct(earningsGrowth5y),
          netProfitMargin: asPct(netProfitMargin),
          roic: asPct(roic),
          cashToDebt: cashToDebt == null ? 'N/A' : cashToDebt.toFixed(2),
          yearsOfProfitability: positiveYears > 0 ? `${positiveYears} years` : 'N/A',
        });
      } catch {
        if (cancelled) return;
        setMetricState({
          revenueGrowth5y: 'N/A',
          earningsGrowth5y: 'N/A',
          netProfitMargin: 'N/A',
          roic: 'N/A',
          cashToDebt: 'N/A',
          yearsOfProfitability: 'N/A',
        });
      }
    };

    void loadMetrics();
    return () => {
      cancelled = true;
    };
  }, [publicApiKey, selectedSymbol]);

  const [metricState, setMetricState] = useState(metricRows);

  const symbolTitle = selectedSymbol || 'No symbol selected';
  const symbolSubtitle = selectedSymbol
    ? `${selectedSymbol} ${(quote?.exchange || '').toUpperCase()}`.trim()
    : 'Search and select a symbol';

  const openLegalModal = (url: string, title: string) => {
    lastFocusedElementRef.current = document.activeElement as HTMLElement | null;
    setLegalModalUrl(url);
    setLegalModalTitle(title);
    setLegalModalDescription(`${title} document dialog`);
  };

  const closeLegalModal = () => {
    setLegalModalUrl('');
    setLegalModalTitle('');
    setLegalModalDescription('');
    const lastFocused = lastFocusedElementRef.current;
    if (lastFocused && typeof lastFocused.focus === 'function') {
      window.setTimeout(() => lastFocused.focus(), 0);
    }
  };

  const styleLegalDocumentScrollbar = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    const documentElement = event.currentTarget.contentDocument;
    if (!documentElement || documentElement.getElementById('digitalgift-legal-scrollbar-style')) return;

    const style = documentElement.createElement('style');
    style.id = 'digitalgift-legal-scrollbar-style';
    style.textContent = `
      html {
        scrollbar-width: thin;
        scrollbar-color: #2F58F2 #DBEAFE;
      }
      html::-webkit-scrollbar {
        width: 12px;
      }
      html::-webkit-scrollbar-track {
        background: #DBEAFE;
        border-radius: 999px;
        margin: 10px 0;
      }
      html::-webkit-scrollbar-thumb {
        background: #2F58F2;
        border-radius: 999px;
        border: 2px solid #DBEAFE;
      }
      html::-webkit-scrollbar-thumb:hover {
        background: #2448CB;
      }
    `;
    documentElement.head.appendChild(style);
  };

  useEffect(() => {
    if (!legalModalUrl) return;

    window.setTimeout(() => {
      legalModalCloseButtonRef.current?.focus();
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (!legalModalUrl) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeLegalModal();
        return;
      }

      if (event.key !== 'Tab') return;
      const panel = legalModalPanelRef.current;
      if (!panel) return;

      const focusable = panel.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }

      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [legalModalUrl]);

  const chartData = useMemo(() => {
    const labels = filteredHistory.map((p) => {
      const d = new Date(p.date);
      return Number.isFinite(d.getTime()) ? String(d.getFullYear()) : p.date;
    });

    const values = filteredHistory.map((p) => p.close);

    return {
      labels,
      datasets: [
        {
          label: `${selectedSymbol || 'Stock'} Price`,
          data: values,
          borderColor: '#2F58F2',
          backgroundColor: 'rgba(47, 88, 242, 0.15)',
          fill: true,
          pointRadius: 0,
          pointHitRadius: 24,
          pointHoverRadius: 5,
          borderWidth: 2,
          tension: 0.2,
        },
      ],
    };
  }, [filteredHistory, selectedSymbol]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
      axis: 'x' as const,
    },
    hover: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    scales: {
      x: {
        ticks: { maxTicksLimit: 8, color: '#667085' },
        grid: { display: false },
      },
      y: {
        position: 'right' as const,
        ticks: {
          color: '#667085',
          callback: (v: any) => `$${v}`,
        },
        grid: { color: '#EEF2F7', drawTicks: false },
      },
    },
  }), []);

  const onSelectResult = async (row: StockSearchResult) => {
    const symbol = String(row.symbol || '').trim().toUpperCase();
    if (!symbol) return;

    const exchange = getSearchResultExchange(row);
    if (!DIGITAL_GIFT_SUPPORTED_EXCHANGES.has(exchange)) {
      setSearchError('Only stocks listed on NYSE or NASDAQ are supported.');
      setSearchResults([]);
      return;
    }

    if (quotaStatus.isBlocked) {
      setSearchResults([]);
      openQuotaReminder(0);
      return;
    }

    setSearchError('');

    if (backendBase && visitorIdRef.current) {
      setQuotaError('');
      try {
        if (!deviceSignalsRef.current) {
          deviceSignalsRef.current = await buildDeviceSignals(allowFunctionalStorage);
        }
        const status = await consumeSearchQuota(backendBase, visitorIdRef.current, deviceSignalsRef.current, query, symbol);
        setQuotaStatus(status);
        scheduleQuotaReminder(status);
      } catch (err: any) {
        const msg = toUserFriendlyError(err, 'Could not update free-search usage right now.');
        setQuotaError(msg);
        if (msg.includes('Too many requests')) {
          setSearchResults([]);
        }
      }
    }

    skipSearchOnceRef.current = true;
    setSelectedSymbol(symbol);
    setQuery(symbol);
    setSearchResults([]);
  };

  const activeWorkshopCtaWindow = WORKSHOP_CTA_WINDOWS.findIndex(
    ({ start, end }) => videoPlayback.currentTime >= start && videoPlayback.currentTime < end
  );
  const showWorkshopCta = activeWorkshopCtaWindow >= 0
    && !dismissedWorkshopCtaWindows.has(activeWorkshopCtaWindow);
  const workshopMarkerPositions = videoPlayback.duration > 0
    ? WORKSHOP_CTA_MARKER_TIMES
      .filter((time) => time < videoPlayback.duration)
      .map((time) => ({ time, left: `${(time / videoPlayback.duration) * 100}%` }))
    : [];
  const isSearchBlocked = quotaStatus.isBlocked;
  const showSearchDropdown = !isSearchBlocked && (isSearching || searchResults.length > 0) && query.trim().length > 0;

  const accessibilityWrapperStyle = useMemo(() => {
    const filters = [
      a11yHighContrast ? 'contrast(1.22) saturate(0.9)' : '',
      a11yInvertColors ? 'invert(1)' : '',
      a11yGrayscale ? 'grayscale(1)' : '',
      a11ySepia ? 'sepia(0.75)' : '',
    ].filter(Boolean).join(' ') || undefined;
    return {
      fontSize: a11yLargeText ? '17px' : undefined,
      zoom: a11yLargeText ? 1.15 : undefined,
      filter: filters,
      fontFamily: a11yReadableFont ? 'Arial, sans-serif' : undefined,
      transition: a11yReduceMotion ? 'none' : undefined,
      lineHeight: a11yLargeText ? 1.58 : undefined,
      cursor: a11yLargeCursor ? 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2732%27 height=%2732%27 viewBox=%270 0 32 32%27%3E%3Cpath d=%27M4 2l5 24 5-7 7 7 3-3-7-7 8-2L4 2z%27 fill=%27white%27 stroke=%27black%27 stroke-width=%272%27/%3E%3C/svg%3E") 4 2, auto' : undefined,
    } as React.CSSProperties;
  }, [a11yGrayscale, a11yHighContrast, a11yInvertColors, a11yLargeCursor, a11yLargeText, a11yReadableFont, a11yReduceMotion, a11ySepia]);

  const resetAccessibilitySettings = () => {
    setA11yLargeText(false);
    setA11yHighContrast(false);
    setA11yReadableFont(false);
    setA11yReduceMotion(false);
    setA11yUnderlineLinks(false);
    setA11yHighlightHeadings(false);
    setA11yInvertColors(false);
    setA11yGrayscale(false);
    setA11ySepia(false);
    setA11yLargeCursor(false);
    setA11yHideImages(false);
    setA11yMarkImages(false);
    setA11yStopAnimations(false);
  };

  const onPickVideoFile = (file: File | null) => {
    if (!file) return;
    if (videoObjectUrl) URL.revokeObjectURL(videoObjectUrl);
    const next = URL.createObjectURL(file);
    setVideoObjectUrl(next);
    setVideoUrl('');
  };

  return (
    <div id="digitalgift-shell" className="digitalgift-shell" style={{ minHeight: '100vh', background: '#EEF4FF', padding: '0 0 1.5rem', boxSizing: 'border-box', position: 'relative', fontFamily: 'Manrope, sans-serif', color: '#0D0D0D', ...accessibilityWrapperStyle }}>
      <style>{`
        @font-face { font-family: 'Heebo'; src: url('/fonts/Heebo-400.ttf') format('truetype'); font-weight: 400; font-style: normal; font-display: swap; }
        @font-face { font-family: 'Heebo'; src: url('/fonts/Heebo-700.ttf') format('truetype'); font-weight: 700; font-style: normal; font-display: swap; }
        @font-face { font-family: 'Heebo'; src: url('/fonts/Heebo-800.ttf') format('truetype'); font-weight: 800; font-style: normal; font-display: swap; }
        @font-face { font-family: 'Heebo'; src: url('/fonts/Heebo-900.ttf') format('truetype'); font-weight: 900; font-style: normal; font-display: swap; }
        .digitalgift-shell { color-scheme: only light; --dg-blue: #2F58F2; --dg-text: #0D0D0D; --dg-border: #E3E8F1; --dg-navy: #02031A; overflow-x: clip; background: #F6F7FB !important; font-family: 'Heebo', sans-serif; }
        .digitalgift-shell button, .digitalgift-shell input { font-family: inherit; }
        .digitalgift-shell input:focus { border-color: var(--dg-blue) !important; box-shadow: 0 0 0 3px rgba(47,88,242,0.14); outline: none; }
        .digitalgift-shell .digitalgift-panel { border-color: var(--dg-border) !important; border-radius: 14px !important; box-shadow: 0 12px 30px rgba(29, 48, 84, 0.06); }
        .digitalgift-shell .digitalgift-heading { color: var(--dg-text) !important; letter-spacing: 0; }
        .digitalgift-shell a, .digitalgift-shell .digitalgift-link, .digitalgift-shell button[data-a11y-link="true"] { color: var(--dg-blue) !important; }
        .digitalgift-shell .digitalgift-quota-reminder-cta { color: #02031A !important; }
        .digitalgift-shell .digitalgift-brand-separator { color: #FFFFFF; font-size: 18px; font-weight: 900; line-height: 1; }
        .digitalgift-shell #digitalgift-value-cta > a { color: #FFFFFF !important; }
        .digitalgift-shell .digitalgift-primary { background: var(--dg-blue) !important; border-color: var(--dg-blue) !important; }
        .digitalgift-shell .digitalgift-header { height: 64px !important; min-height: 64px !important; background: #02031A; color: #FFFFFF; border-bottom: 0; max-width: none !important; width: 100%; padding: 0 48px !important; }
        .digitalgift-shell .digitalgift-desktop-topbar { position: sticky; top: 0; z-index: 1000; display: none; height: 64px; min-height: 64px; width: 100%; box-sizing: border-box; align-items: center; justify-content: space-between; gap: 24px; padding: 0 48px; background: #02031A; color: #FFFFFF; border-bottom: 1px solid rgba(255,255,255,0.45); }
        .digitalgift-shell .digitalgift-header-meta { display: flex; align-items: center; gap: 24px; overflow: hidden; }
        .digitalgift-shell .digitalgift-mobile-next-copy { display: none; }
        .digitalgift-shell .digitalgift-mobile-stage-title { display: none; }
        .digitalgift-shell .digitalgift-mobile-disclaimer { display: none; }
        .digitalgift-shell .digitalgift-mobile-intro-copy { display: none; }
        .digitalgift-shell .digitalgift-mobile-card-copy { display: none; }
        .digitalgift-shell .digitalgift-header-meta > div { color: #A7ADC2; font-size: 15px; line-height: 1.5; font-weight: 400; white-space: nowrap; }
        .digitalgift-shell .digitalgift-header-meta > a { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto !important; width: auto !important; height: auto !important; box-sizing: border-box; padding: 7px 16px !important; background: #02031A !important; border: 2px solid #FFFFFF !important; border-radius: 10px !important; color: #FFFFFF !important; font-family: 'Heebo', sans-serif !important; font-size: 15px !important; line-height: 1.2 !important; font-weight: 700 !important; white-space: nowrap; }
        .digitalgift-shell .digitalgift-yellow-link { color: #FFFFFF !important; }
        .digitalgift-shell .digitalgift-main { position: relative; z-index: 2; display: grid !important; grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr) !important; gap: 48px !important; margin: -56px auto 0 !important; padding: 48px; width: min(1184px, calc(100% - 40px)); max-width: 1184px !important; min-width: 0; background: #FFFFFF; border-radius: 24px; box-shadow: 0 1px 2px rgba(2,3,26,0.04), 0 12px 40px rgba(2,3,26,0.06); }
        .digitalgift-shell .digitalgift-sheet-divider { grid-column: 2; grid-row: 1; align-self: stretch; width: 1px; background: #E3E6EE; }
        .digitalgift-shell .digitalgift-stock-panel { order: 1; grid-column: 1; grid-row: 1; margin: 0 !important; border: 0 !important; border-radius: 0 !important; padding: 0 !important; box-shadow: none !important; min-width: 0; box-sizing: border-box; }
        .digitalgift-shell .digitalgift-stock-panel > h1 { display: flex !important; flex-direction: column; align-items: flex-end !important; justify-content: flex-start !important; gap: 6px !important; margin: 0 !important; font-size: 26px !important; line-height: 1.15 !important; }
        .digitalgift-shell .digitalgift-stock-panel { display: flex !important; flex-direction: column; gap: 24px; }
        .digitalgift-shell .digitalgift-stock-panel > h1 { order: 1; }
        .digitalgift-shell .digitalgift-stock-panel > .digitalgift-search-wrap { order: 2; }
        .digitalgift-shell .digitalgift-stock-panel > .digitalgift-quote-panel { order: 3; }
        .digitalgift-shell .digitalgift-stock-panel > #digitalgift-stock-chart { order: 4; }
        .digitalgift-shell .digitalgift-stock-panel > #digitalgift-metrics { order: 5; }
        .digitalgift-shell .digitalgift-stock-panel > h1 > span:last-child { font-size: 15px !important; line-height: 1.5 !important; white-space: nowrap; }
        .digitalgift-shell .digitalgift-search-wrap { width: 100% !important; margin-bottom: 24px !important; }
        .digitalgift-shell .digitalgift-search-wrap > div:nth-child(2) { display: block !important; }
        .digitalgift-shell .digitalgift-search-wrap input { width: 100% !important; max-width: none !important; height: 52px !important; border: 1px solid #E3E6EE !important; border-radius: 999px !important; padding: 0 48px 0 20px !important; direction: rtl !important; text-align: right !important; font-size: 15px !important; color: #646A7E !important; }
        .digitalgift-shell .digitalgift-search-wrap .digitalgift-search-icon { position: absolute; left: auto; right: 11px; top: 50%; width: 20px; height: 20px; transform: translateY(-50%); pointer-events: none; z-index: 1; }
        .digitalgift-shell .digitalgift-search-suggestions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; width: 100%; margin-top: 12px; direction: ltr; color: #646A7E; font-family: 'Heebo', sans-serif; font-size: 13px; line-height: 1.5; }
        .digitalgift-shell .digitalgift-search-suggestions > span { order: 6; direction: rtl; white-space: nowrap; font-size: 15px; line-height: 1.5; }
        .digitalgift-shell .digitalgift-search-suggestions button { border: 0; border-radius: 999px; padding: 4px 12px; background: #F1F3F8; color: #02031A; font-family: 'Manrope', sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; }
        .digitalgift-shell .digitalgift-quote-panel { display: grid !important; grid-template-columns: auto minmax(0, 1fr) 48px !important; align-items: center; column-gap: 16px; direction: ltr; width: 100% !important; min-height: 0 !important; margin: 0 !important; padding: 24px 0 0 !important; border: 0 !important; border-top: 1px solid #E3E6EE !important; border-radius: 0 !important; background: transparent !important; box-shadow: none !important; }
        .digitalgift-shell .digitalgift-quote-company { grid-column: 2; grid-row: 1; min-width: 0; overflow: hidden; text-align: right; }
        .digitalgift-shell .digitalgift-quote-company > div:first-child { font-family: 'Heebo', sans-serif; font-size: 24px !important; line-height: 1.25; font-weight: 800 !important; color: #02031A !important; white-space: normal; overflow: visible; overflow-wrap: anywhere; }
        .digitalgift-shell .digitalgift-quote-company > div:last-child { font-family: 'Heebo', sans-serif; font-size: 15px !important; line-height: 1.5; color: #646A7E !important; white-space: nowrap; }
        .digitalgift-shell .digitalgift-quote-price { grid-column: 1; grid-row: 1; display: flex; flex-direction: column; align-items: flex-start; font-family: 'Manrope', sans-serif; font-size: 24px !important; line-height: 1.2; font-weight: 800 !important; color: #02031A !important; white-space: nowrap; }
        .digitalgift-shell .digitalgift-quote-price small { color: #0E8A5F; font-size: 14px; line-height: 1.5; font-weight: 700; }
        .digitalgift-shell .digitalgift-quote-logo { grid-column: 3; grid-row: 1; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; border-radius: 12px; background: #02031A; color: #FFFFFF; font-family: 'Manrope', sans-serif; font-size: 18px; font-weight: 800; }
        .digitalgift-shell .digitalgift-metrics { max-width: none !important; margin: 0 0 24px !important; padding-top: 0 !important; border-top: 0 !important; display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
        .digitalgift-shell .digitalgift-metrics > div { display: contents !important; }
        .digitalgift-shell .digitalgift-metrics > div > div { display: flex !important; flex-direction: column; align-items: flex-start; justify-content: flex-start; gap: 4px; width: auto; height: 133px; box-sizing: border-box; padding: 16px 18px !important; border: 1px solid #E3E6EE !important; border-radius: 16px; text-align: left !important; }
        .digitalgift-shell .digitalgift-metrics > div > div > span { display: block; width: 100%; padding: 0 !important; text-align: left !important; }
        .digitalgift-shell .digitalgift-metrics > div > div > span:first-child { font-family: 'Manrope', sans-serif; color: #02031A !important; font-size: 13px !important; line-height: 1.3; font-weight: 700 !important; }
        .digitalgift-shell .digitalgift-metrics > div > div > span:nth-child(2) { color: #02031A !important; font-family: 'Manrope', sans-serif; font-size: 30px !important; line-height: 1.15; font-weight: 800 !important; }
        .digitalgift-shell .digitalgift-metrics > div > div > span:nth-child(3) { display: none; }
        .digitalgift-shell .digitalgift-metrics > div > div > span:last-child { color: #646A7E !important; font-family: 'Manrope', sans-serif; font-size: 13px !important; line-height: 1.5; text-align: left !important; }
        .digitalgift-shell .digitalgift-metrics > div:last-child { display: block !important; grid-column: 1 / -1; padding-top: 0 !important; color: #646A7E !important; font-family: 'Heebo', sans-serif; font-size: 15px !important; line-height: 1.5; text-align: right !important; }
        .digitalgift-shell .digitalgift-stock-cta { order: 6; display: flex; align-items: center; justify-content: space-between; gap: 16px; width: 100%; padding-top: 18px; border-top: 1px solid #E3E6EE; direction: rtl; }
        .digitalgift-shell .digitalgift-stock-cta p { margin: 0; color: #02031A; font-family: 'Heebo', sans-serif; font-size: 17px; line-height: 1.6; text-align: right; }
        .digitalgift-shell .digitalgift-stock-cta a { flex: 0 0 auto; color: #2468FF; font-family: 'Heebo', sans-serif; font-size: 15px; line-height: 1.5; font-weight: 700; text-decoration: none; white-space: nowrap; }
        .digitalgift-shell #digitalgift-stock-chart { display: flex !important; flex-direction: column; width: 100% !important; max-width: none !important; margin: 0 !important; padding: 0 !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; }
        .digitalgift-shell #digitalgift-stock-chart > div:first-child { display: block !important; margin: 0 0 16px !important; color: #646A7E; font-family: 'Manrope', sans-serif; font-size: 13px !important; }
        .digitalgift-shell #digitalgift-stock-chart > div:first-child h2 { margin-bottom: 4px !important; font-size: 13px !important; line-height: 1.3; letter-spacing: 0.78px; color: #646A7E !important; }
        .digitalgift-shell #digitalgift-stock-chart > div:first-child span { display: inline; margin-right: 6px; font-weight: 500 !important; }
        .digitalgift-shell #digitalgift-stock-chart > div:nth-child(2) { display: flex !important; align-self: flex-start; gap: 4px; margin: 0 0 16px !important; padding: 4px; border-radius: 999px; background: #F1F3F8; }
        .digitalgift-shell #digitalgift-stock-chart > div:nth-child(2) button { border: 0 !important; border-radius: 999px !important; padding: 5px 12px !important; background: transparent !important; color: #646A7E !important; font-family: 'Manrope', sans-serif; font-size: 13px !important; line-height: normal; }
        .digitalgift-shell #digitalgift-stock-chart > div:nth-child(2) button.is-selected { background: #02031A !important; color: #FFFFFF !important; }
        .digitalgift-shell #digitalgift-stock-chart .digitalgift-range-all { display: none !important; }
        .digitalgift-shell #digitalgift-chart-frame { height: 130px !important; }
        .digitalgift-shell .digitalgift-video-panel { order: 3; grid-column: 3; grid-row: 1; display: flex !important; flex-direction: column; gap: 16px; background: #FFFFFF !important; border: 0 !important; border-radius: 0 !important; padding: 0 !important; box-shadow: none !important; min-width: 0; box-sizing: border-box; }
        .digitalgift-shell .digitalgift-video-panel .digitalgift-video { height: 315px !important; }
        .digitalgift-shell .digitalgift-video-copy { order: 1; min-height: 0 !important; padding: 0 !important; }
        .digitalgift-shell .digitalgift-video-copy > div:first-child { margin-bottom: 6px !important; font-size: 26px !important; line-height: 1.15 !important; color: #02031A !important; }
        .digitalgift-shell .digitalgift-video-copy h2 { display: none; }
        .digitalgift-shell .digitalgift-video-copy p:first-of-type { margin: 0 !important; font-size: 15px !important; line-height: 1.5 !important; color: #646A7E !important; }
        .digitalgift-shell .digitalgift-video-copy p:not(:first-of-type), .digitalgift-shell .digitalgift-video-copy a { display: none !important; }
        .digitalgift-shell .digitalgift-video-slot { order: 2; height: 315px !important; }
        .digitalgift-shell .digitalgift-video-links { order: 3; }
        .digitalgift-shell .digitalgift-video-chapters { order: 4; }
        .digitalgift-shell .digitalgift-video-poster { overflow: hidden; border-radius: 16px !important; background: #0B1024 !important; }
        .digitalgift-shell .digitalgift-video-poster-grid,
        .digitalgift-shell .digitalgift-video-poster-chart { position: absolute; display: block; pointer-events: none; max-width: none; }
        .digitalgift-shell .digitalgift-video-poster-grid { inset: 0; width: 100%; height: 100%; }
        .digitalgift-shell .digitalgift-video-poster-chart { left: 0; right: 0; bottom: 0; width: 100%; height: 140px; }
        .digitalgift-shell .digitalgift-video-poster > div:first-of-type { display: none; }
        .digitalgift-shell .digitalgift-video-poster-title { position: absolute; top: 50px; right: 34px; width: 330px; display: flex; flex-direction: column; align-items: flex-start; gap: 8px; text-align: right; direction: rtl; }
        .digitalgift-shell .digitalgift-video-poster-title > div { color: #7FA4FF; font-family: 'Heebo', sans-serif; font-size: 13px; line-height: 1.3; font-weight: 700; letter-spacing: 0.78px; }
        .digitalgift-shell .digitalgift-video-poster-title > strong { color: #FFFFFF; font-family: 'Heebo', sans-serif; font-size: 24px; line-height: 1.25; font-weight: 800; }
        .digitalgift-shell .digitalgift-video-poster-title > span { color: #A7ADC2; font-family: 'Heebo', sans-serif; font-size: 15px; line-height: 1.5; white-space: nowrap; }
        .digitalgift-shell .digitalgift-video-poster-topline,
        .digitalgift-shell .digitalgift-video-poster-controls { display: none !important; }
        .digitalgift-shell .digitalgift-video-poster-caption { display: none !important; }
        .digitalgift-shell .digitalgift-video-poster-play { position: absolute; left: 34px; top: 205px; width: 76px; height: 76px; display: flex; align-items: center; justify-content: center; border-radius: 999px; background: #FFFFFF; box-shadow: 0 10px 30px rgba(0,0,0,0.35); }
        .digitalgift-shell .digitalgift-video-poster-play img { display: block; width: 28px; height: 28px; }
        .digitalgift-shell .digitalgift-floating-video .digitalgift-video-poster-title { top: 42px; right: 24px; width: 300px; gap: 4px; }
        .digitalgift-shell .digitalgift-floating-video .digitalgift-video-poster-title > div { font-size: 11px; line-height: 1.3; }
        .digitalgift-shell .digitalgift-floating-video .digitalgift-video-poster-title > strong { font-size: 20px; line-height: 1.18; }
        .digitalgift-shell .digitalgift-floating-video .digitalgift-video-poster-title > span { font-size: 12px; line-height: 1.4; }
        .digitalgift-shell .digitalgift-floating-video .digitalgift-video-poster-play { top: auto; bottom: 18px; left: 24px; width: 64px; height: 64px; z-index: 2; }
        .digitalgift-shell .digitalgift-floating-video .digitalgift-video-poster-play img { width: 24px; height: 24px; }
        .digitalgift-shell .digitalgift-video-poster > div[aria-hidden="true"] { display: none !important; }
        .digitalgift-shell .digitalgift-workshop-markers { position: absolute; left: 15%; right: 15%; bottom: 22px; height: 12px; z-index: 4; pointer-events: none; }
        .digitalgift-shell .digitalgift-workshop-markers > span { position: absolute; top: 50%; width: 6px; height: 14px; border-radius: 999px; background: #F7C948; transform: translate(-50%, -50%); }
        .digitalgift-shell .digitalgift-workshop-overlay { position: absolute; top: 14px; left: 14px; z-index: 5; display: flex; flex-direction: row; align-items: center; gap: 12px; width: min(467px, calc(100% - 28px)); min-height: 60px; padding: 12px 14px; box-sizing: border-box; border-radius: 12px; background: #FFFFFF; box-shadow: 0 10.6179px 26.5446px rgba(0, 0, 0, 0.3); direction: rtl; }
        .digitalgift-shell .digitalgift-workshop-overlay-copy { display: flex; flex: 1 1 auto; flex-direction: column; min-width: 0; gap: 2px; text-align: right; }
        .digitalgift-shell .digitalgift-workshop-overlay-copy strong { color: #02031A; font-size: 16px; line-height: 1.15; font-weight: 800; white-space: nowrap; }
        .digitalgift-shell .digitalgift-workshop-overlay-copy span { color: #73788C; font-size: 13px; line-height: 1.25; white-space: nowrap; }
        .digitalgift-shell .digitalgift-workshop-overlay-cta { display: inline-flex; flex: 0 0 auto; align-items: center; justify-content: center; padding: 8px 18px; border-radius: 999px; background: #F7C948; color: #02031A !important; font-size: 16px; line-height: 1.2; font-weight: 800; text-decoration: none; white-space: nowrap; }
        .digitalgift-shell .digitalgift-workshop-overlay-close { display: grid; flex: 0 0 24px; place-items: center; width: 24px; height: 24px; border: 0; padding: 0; background: transparent; color: #73788C; font-size: 22px; line-height: 1; cursor: pointer; }
        .digitalgift-shell #digitalgift-mobile-workshop-strip { display: none; }
        .digitalgift-shell .digitalgift-video-links { display: flex; align-items: center; justify-content: flex-start; gap: 24px; width: 100%; margin-top: 8px; }
        .digitalgift-shell .digitalgift-video-links button,
        .digitalgift-shell .digitalgift-video-links a { display: inline-flex; align-items: center; gap: 6px; direction: ltr; border: 0; padding: 0; background: transparent; color: #2468FF !important; font-family: 'Heebo', sans-serif; font-size: 15px; line-height: 1.5; font-weight: 700; text-decoration: none; cursor: pointer; white-space: nowrap; }
        .digitalgift-shell .digitalgift-video-links a { direction: rtl; unicode-bidi: plaintext; }
        .digitalgift-shell .digitalgift-video-links img { width: 16px; height: 16px; display: block; flex: 0 0 16px; }
        .digitalgift-shell .digitalgift-video-chapters { display: flex; flex-direction: column; align-items: stretch; width: 100%; }
        .digitalgift-shell .digitalgift-video-chapters-toggle { display: none; }
        .digitalgift-shell .digitalgift-video-chapters-list { display: contents; }
        .digitalgift-shell .digitalgift-video-chapter { display: flex; flex-direction: row; align-items: center; gap: 16px; min-height: 54px; border-bottom: 1px solid #E3E6EE; }
        .digitalgift-shell .digitalgift-video-chapter > span { order: 1; color: #646A7E; font-family: 'Manrope', sans-serif; font-size: 13px; line-height: 1.5; white-space: nowrap; }
        .digitalgift-shell .digitalgift-video-chapter > button { order: 2; flex: 1; border: 0; padding: 0; background: transparent; color: #02031A; font-family: 'Heebo', sans-serif; font-size: 17px; line-height: 1.5; text-align: right; cursor: pointer; }
        .digitalgift-shell .digitalgift-floating-video .digitalgift-video { height: 100% !important; }
        .digitalgift-shell .digitalgift-floating-video iframe.digitalgift-video { width: 100% !important; height: 100% !important; transform: none; }
        .digitalgift-shell .digitalgift-floating-video { max-width: calc(100vw - 24px) !important; max-height: calc(100vh - 24px) !important; }
        .digitalgift-shell .digitalgift-main.is-video-floating,
        .digitalgift-shell .digitalgift-video-panel.is-video-floating { z-index: auto !important; }
        .digitalgift-shell .digitalgift-video-slot.is-video-floating { position: relative !important; z-index: 2147483647 !important; pointer-events: none !important; }
        .digitalgift-shell #digitalgift-player-frame:fullscreen { position: fixed !important; inset: 0 !important; width: 100vw !important; height: 100vh !important; height: 100dvh !important; max-width: none !important; max-height: none !important; z-index: 2147483647 !important; border: 0 !important; border-radius: 0 !important; background: #000 !important; }
        .digitalgift-shell #digitalgift-player-frame:fullscreen > .digitalgift-video { display: block !important; width: 100% !important; height: 100% !important; }
        .digitalgift-shell .digitalgift-search-dropdown { right: auto !important; width: 310px !important; max-width: 100% !important; }
        .digitalgift-shell .digitalgift-intro { display: flex; flex-direction: column; align-items: flex-start; gap: 16px; background: #02031A; color: #FFFFFF; max-width: none !important; width: 100%; margin: 0 !important; padding: 48px !important; padding-bottom: 104px !important; }
        .digitalgift-shell .digitalgift-intro > div { margin: 0 !important; }
        .digitalgift-shell .digitalgift-intro > h1,
        .digitalgift-shell .digitalgift-intro > p,
        .digitalgift-shell .digitalgift-intro > div:last-child { margin: 0 !important; }
        .digitalgift-shell .digitalgift-intro > h1 { width: 900px !important; max-width: 100%; }
        .digitalgift-shell .digitalgift-intro > p { width: 620px !important; max-width: 100%; }
        .digitalgift-shell .digitalgift-intro > div:last-child { padding-top: 16px; direction: ltr; display: flex !important; align-items: flex-end; gap: 32px; }
        .digitalgift-shell .digitalgift-intro > div:last-child > span { display: flex; align-items: center; gap: 12px; direction: rtl; white-space: nowrap; }
        .digitalgift-shell .digitalgift-intro > div:last-child > span > b { display: inline-flex !important; align-items: center; justify-content: center; flex: 0 0 56px; width: 56px !important; height: 36px !important; margin: 0 !important; padding: 0 !important; border: 0 !important; border-radius: 999px; background: #2468FF; color: #FFFFFF; font-family: 'Manrope', sans-serif; font-size: 20px !important; font-weight: 800 !important; line-height: 1; }
        .digitalgift-shell .digitalgift-content-section { max-width: 1184px !important; background: #FFFFFF; border: 1px solid #E3E8F1; border-radius: 24px !important; box-shadow: 0 12px 30px rgba(29, 48, 84, 0.05); }
        .digitalgift-shell #digitalgift-content-grid { display: flex !important; flex-direction: column; align-items: flex-end; gap: 0 !important; min-height: 0 !important; }
        .digitalgift-shell #digitalgift-content-grid > div:first-child { width: 100%; margin-top: 24px; text-align: right; }
        .digitalgift-shell #digitalgift-content-grid > div:first-child p { width: min(100%, 620px); margin-left: auto !important; font-size: 18px !important; line-height: 1.6 !important; }
        .digitalgift-shell #digitalgift-content-grid > div:last-child { width: 100%; text-align: right; }
        .digitalgift-shell #digitalgift-content-grid h2 { width: min(100%, 760px); margin-left: auto !important; font-size: 42px !important; line-height: 1.15 !important; }
        .digitalgift-shell #digitalgift-content-grid .digitalgift-next-eyebrow { margin: 0 0 20px; color: #2468FF; font-family: 'Heebo', sans-serif; font-size: 14px; line-height: 1.3; font-weight: 700; letter-spacing: 0.78px; }
        .digitalgift-shell .digitalgift-learn-cards { position: relative; column-gap: 48px !important; }
        .digitalgift-shell .digitalgift-learn-cards > div { min-height: 172px; box-sizing: border-box; border-radius: 20px !important; padding: 32px !important; }
        .digitalgift-shell .digitalgift-learn-card-title { margin-bottom: 16px !important; font-family: 'Heebo', sans-serif; font-size: 13px; line-height: 1.3; }
        .digitalgift-shell .digitalgift-learn-card-row { display: flex; align-items: flex-start; justify-content: flex-start; gap: 12px; min-height: 30px; font-family: 'Heebo', sans-serif; font-size: 17px; line-height: 1.5; text-align: right; }
        .digitalgift-shell .digitalgift-learn-card-check { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 18px; width: 18px; height: 18px; border-radius: 999px; background: #DDF7EA; color: #0E8A5F; font-family: Arial, sans-serif; font-size: 13px; font-weight: 700; line-height: 1; }
        .digitalgift-shell .digitalgift-learn-card-arrow { display: block; flex: 0 0 22px; width: 22px; height: 22px; }
        .digitalgift-shell #digitalgift-value-cta { width: calc(100% + 144px) !important; margin-top: 48px !important; margin-left: -72px !important; margin-right: -72px !important; min-height: 342px !important; grid-template-columns: 452px minmax(0, 1fr) !important; grid-template-rows: 1fr !important; column-gap: 0 !important; border-radius: 0 0 28px 28px !important; }
        .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter { grid-column: 1 !important; grid-row: 1 !important; height: 342px !important; min-height: 342px !important; overflow: hidden; }
        .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter > img { height: 100% !important; object-fit: cover !important; object-position: center 20% !important; }
        .digitalgift-shell #digitalgift-footer { position: relative; max-width: 1184px !important; margin: 48px auto 0 !important; padding: 104px 0 48px !important; background: transparent !important; border: none !important; border-radius: 0 !important; display: flex !important; flex-direction: column !important; align-items: flex-end !important; justify-content: flex-start !important; gap: 32px !important; }
        .digitalgift-shell #digitalgift-footer::before { content: ''; position: absolute; top: 72px; left: 0; right: 0; height: 1px; background: #E3E8F1; }
        .digitalgift-shell #digitalgift-value-disclaimer { width: 980px !important; max-width: 100% !important; color: #646A7E !important; font-size: 13px !important; line-height: 1.5 !important; text-align: right !important; }
        .digitalgift-shell #digitalgift-footer-links { display: flex !important; align-items: flex-start !important; justify-content: flex-start !important; gap: 32px !important; }
        .digitalgift-shell #digitalgift-footer-links button { color: #646A7E !important; font-size: 15px !important; font-weight: 700 !important; }
        .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-body { grid-column: 2 !important; grid-row: 1 !important; align-self: stretch !important; display: flex; flex-direction: column; align-items: flex-start; justify-content: flex-start; gap: 24px; padding: 48px 72px !important; box-sizing: border-box; direction: rtl; }
        .digitalgift-shell .digitalgift-registration-heading { display: flex; flex-direction: column; align-items: flex-start; align-self: flex-start; width: 100%; margin-left: 0; gap: 8px; text-align: right !important; }
        .digitalgift-shell .digitalgift-registration-heading > div:first-child { color: #F2C94C; font-size: 13px; line-height: 1.3; font-weight: 700; letter-spacing: 0.78px; }
        .digitalgift-shell .digitalgift-registration-heading .digitalgift-registration-title { color: #FFFFFF; font-size: 24px; line-height: 1.25; font-weight: 800; }
        .digitalgift-shell .digitalgift-registration-attribution { color: #D6DAE6; font-size: 16px; line-height: 1.5; font-weight: 400; }
        .digitalgift-shell .digitalgift-registration-attribution strong { color: #FFFFFF; font-weight: 800; }
        .digitalgift-shell .digitalgift-registration-facts { display: flex !important; gap: 48px !important; align-items: flex-start; align-self: flex-start; width: max-content; margin-left: 0; direction: rtl; text-align: right !important; }
        .digitalgift-shell .digitalgift-registration-facts > span { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; color: #FFFFFF; font-size: 19px !important; line-height: 1.2; font-weight: 800; text-align: right; }
        .digitalgift-shell .digitalgift-registration-facts > span > span { color: #D6DAE6; font-size: 15px; line-height: 1.5; font-weight: 400; }
        .digitalgift-shell .digitalgift-registration-action { display: flex; flex-direction: column; align-items: center; align-self: stretch; gap: 10px; width: 100%; text-align: center; }
        .digitalgift-shell .digitalgift-registration-action .digitalgift-workshop-link { width: 100%; box-sizing: border-box; margin: 0 !important; padding: 16px 32px !important; color: #1A1606 !important; font-size: 18px; line-height: 1.2; font-weight: 800; }
        .digitalgift-shell .digitalgift-registration-action > div { margin: 0 !important; color: #A7ADC2 !important; font-size: 13px !important; line-height: 1.5; }
        .digitalgift-shell .digitalgift-testimonial { display: flex; flex-direction: column; align-items: flex-start; align-self: stretch; gap: 12px; width: 100%; box-sizing: border-box; padding: 24px; border: 1.5px dashed rgba(255,255,255,0.18); border-radius: 20px; background: #02031A; }
        .digitalgift-shell .digitalgift-testimonial > img { width: 28px; height: 28px; display: block; }
        .digitalgift-shell .digitalgift-testimonial > p { min-width: 100%; margin: 0; color: #FFFFFF; font-size: 18px; line-height: 1.6; text-align: right; }
        .digitalgift-shell .digitalgift-testimonial-author { display: flex; flex-direction: row-reverse; align-items: center; gap: 12px; }
        .digitalgift-shell .digitalgift-testimonial-author > div { display: flex; flex-direction: column; align-items: flex-start; color: #FFFFFF; font-size: 15px; line-height: 1.2; }
        .digitalgift-shell .digitalgift-testimonial-author > div span { color: #A7ADC2; font-size: 13px; line-height: 1.5; white-space: nowrap; }
        .digitalgift-shell .digitalgift-testimonial-author > img { width: 40px; height: 40px; border-radius: 999px; display: block; }
        .digitalgift-shell #digitalgift-mobile-bar { display: none; }
        .digitalgift-shell .digitalgift-learn-cards::after { content: ''; position: absolute; top: 50%; left: 50%; width: 28px; height: 28px; transform: translate(-50%, -50%); background: url('/images/digitalgift-card-arrow.svg') center / 28px 28px no-repeat; pointer-events: none; }
        @media (max-width: 860px) {
          .digitalgift-shell { padding: 0 0 1rem !important; }
          .digitalgift-shell #digitalgift-stock-chart > div:first-child .digitalgift-stock-price-stats { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; width: 100%; }
          .digitalgift-shell #digitalgift-stock-chart > div:first-child .digitalgift-stock-price-return { display: block; white-space: nowrap; }
          .digitalgift-shell #digitalgift-stock-chart > div:first-child .digitalgift-stock-price-levels { display: flex; flex-wrap: nowrap; align-items: baseline; gap: 8px; width: 100%; white-space: nowrap; font-size: 12px !important; }
          .digitalgift-shell #digitalgift-stock-chart > div:first-child .digitalgift-stock-price-levels > span { margin-right: 0; }
          .digitalgift-shell .digitalgift-layout { grid-template-columns: 1fr !important; margin-top: -56px !important; padding: 24px 16px !important; width: 351px !important; max-width: calc(100% - 24px) !important; gap: 48px !important; box-sizing: border-box; }
          .digitalgift-shell .digitalgift-sheet-divider { display: none; }
          .digitalgift-shell .digitalgift-panel { padding: 0.875rem !important; }
          .digitalgift-shell .digitalgift-stock-panel { grid-column: 1; grid-row: 2; order: 2; position: relative; z-index: 1; border-radius: 14px !important; padding: 0 !important; }
          .digitalgift-shell .digitalgift-content-section { width: 351px !important; max-width: calc(100% - 24px) !important; margin: 72px auto 0 !important; padding: 32px 20px 24px !important; border-radius: 20px !important; box-sizing: border-box; direction: rtl !important; text-align: right !important; }
          .digitalgift-shell #digitalgift-content-grid { display: flex !important; flex-direction: column !important; align-items: stretch !important; gap: 0 !important; min-height: 0 !important; }
          .digitalgift-shell #digitalgift-content-grid > div:first-child { order: 2 !important; width: 100% !important; margin-top: 0 !important; text-align: right !important; }
          .digitalgift-shell #digitalgift-content-grid > div:first-child p { width: 100% !important; margin: 0 !important; font-size: 17px !important; line-height: 1.6 !important; }
          .digitalgift-shell #digitalgift-content-grid > div:last-child { order: 1 !important; width: 100% !important; text-align: right !important; }
          .digitalgift-shell #digitalgift-content-grid > div:last-child .digitalgift-next-eyebrow { margin: 0 0 16px !important; font-size: 13px !important; line-height: 1.3 !important; }
          .digitalgift-shell #digitalgift-content-grid > div:last-child h2 { width: 100% !important; margin: 0 !important; font-size: 28px !important; line-height: 1.15 !important; text-align: right !important; }
          .digitalgift-shell .digitalgift-learn-cards { display: flex !important; flex-direction: column !important; gap: 56px !important; margin-top: 16px !important; }
          .digitalgift-shell .digitalgift-learn-cards::after { display: none !important; }
          .digitalgift-shell .digitalgift-learn-cards > div { position: relative; width: 100%; min-height: 0 !important; padding: 20px !important; border-radius: 20px !important; direction: rtl !important; text-align: right !important; }
          .digitalgift-shell .digitalgift-learn-cards > div:first-child::after { content: ''; position: absolute; left: 50%; bottom: -42px; width: 28px; height: 28px; transform: translateX(-50%) rotate(-90deg); background: url('/images/digitalgift-card-arrow.svg') center / 28px 28px no-repeat; }
          .digitalgift-shell .digitalgift-learn-card-title { margin-bottom: 24px !important; font-size: 13px !important; line-height: 1.3 !important; text-align: right !important; }
          .digitalgift-shell .digitalgift-learn-card-row { gap: 12px; min-height: 0; margin: 0 !important; font-size: 17px !important; line-height: 1.6 !important; direction: rtl !important; text-align: right !important; }
          .digitalgift-shell .digitalgift-learn-card-row + .digitalgift-learn-card-row { margin-top: 16px !important; }
          .digitalgift-shell .digitalgift-learn-card-check,
          .digitalgift-shell .digitalgift-learn-card-arrow { flex-basis: 22px; width: 22px; height: 22px; }
          .digitalgift-shell #digitalgift-value-cta { width: 100% !important; margin-left: 0 !important; margin-right: 0 !important; }
          .digitalgift-shell #digitalgift-value-cta { display: flex !important; flex-direction: column !important; width: calc(100% + 40px) !important; min-height: 0 !important; margin: 24px -20px 0 !important; padding: 0 !important; border-radius: 0 !important; background: #F6F7FB !important; direction: rtl !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-body { order: 1 !important; width: 100% !important; box-sizing: border-box; padding: 32px 20px !important; gap: 20px !important; align-items: flex-end !important; background: #05051F !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter { order: 2 !important; width: 100% !important; min-height: 0 !important; height: auto !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter > img { width: 100% !important; height: auto !important; object-fit: contain !important; object-position: center top !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter { border-radius: 0 0 20px 20px !important; background: #F6F7FB !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-heading { align-items: flex-start !important; gap: 8px !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-heading > div:first-child { font-size: 13px !important; line-height: 1.3 !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-heading .digitalgift-registration-title { font-size: 24px !important; line-height: 1.25 !important; text-align: right !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-facts { gap: 16px 24px !important; align-items: flex-start !important; justify-content: flex-start !important; width: 100% !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-facts > span { font-size: 19px !important; line-height: 1.2 !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-facts > span > span { font-size: 15px !important; line-height: 1.5 !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-action { gap: 10px !important; width: 100% !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-action .digitalgift-workshop-link { width: 100% !important; min-height: 54px !important; box-sizing: border-box; margin: 0 !important; padding: 16px 32px !important; border-radius: 999px !important; background: #F7C948 !important; color: #1A1606 !important; font-family: 'Heebo', sans-serif; font-size: 18px !important; line-height: 1.2 !important; font-weight: 800 !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-action > div { font-size: 13px !important; line-height: 1.5 !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-testimonial { gap: 12px !important; width: 100% !important; padding: 24px !important; border: 1.5px dashed rgba(255,255,255,0.18) !important; border-radius: 20px !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-testimonial,
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-note { display: none !important; }
          .digitalgift-shell #digitalgift-mobile-bar { position: fixed; left: 0; right: 0; bottom: 0; z-index: 1100; display: flex !important; align-items: center; gap: 16px; min-height: 44px; box-sizing: border-box; padding: 12px 20px; background: #02031A; color: #FFFFFF; direction: ltr !important; }
          .digitalgift-shell #digitalgift-mobile-bar a { flex: 0 0 auto; padding: 9px 18px; border-radius: 999px; background: #F7C948 !important; color: #02031A !important; font-family: 'Heebo', sans-serif; font-size: 15px; line-height: 1.2; font-weight: 700; text-decoration: none; }
          .digitalgift-shell #digitalgift-mobile-bar span { flex: 1 1 auto; color: #FFFFFF; font-family: 'Heebo', sans-serif; font-size: 15px; line-height: 1.2; font-weight: 700; text-align: right; direction: rtl; }
          .digitalgift-shell #digitalgift-footer { display: flex !important; width: 351px !important; max-width: calc(100% - 24px) !important; box-sizing: border-box; margin: 0 auto !important; padding: 24px 12px 96px !important; border: 0 !important; gap: 24px !important; align-items: flex-end !important; background: transparent !important; text-align: right !important; direction: rtl !important; }
          .digitalgift-shell #digitalgift-footer::before { display: none !important; }
          .digitalgift-shell #digitalgift-value-disclaimer { width: 100% !important; color: #646A7E !important; font-size: 13px !important; line-height: 1.5 !important; text-align: right !important; }
          .digitalgift-shell #digitalgift-footer-links { display: flex !important; flex-wrap: wrap !important; justify-content: flex-end !important; gap: 8px 20px !important; width: 100% !important; direction: ltr !important; }
          .digitalgift-shell #digitalgift-footer-links button:nth-child(4) { order: 1; }
          .digitalgift-shell #digitalgift-footer-links button:nth-child(3) { order: 2; }
          .digitalgift-shell #digitalgift-footer-links button:nth-child(2) { order: 3; }
          .digitalgift-shell #digitalgift-footer-links button:nth-child(1) { order: 4; }
          .digitalgift-shell #digitalgift-footer-links button { width: auto !important; min-height: 0 !important; color: #646A7E !important; font-size: 15px !important; line-height: 1.2 !important; font-weight: 700 !important; }
          .digitalgift-shell .digitalgift-metrics { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
          .digitalgift-shell .digitalgift-metrics { margin-bottom: 0 !important; }
          .digitalgift-shell .digitalgift-metrics > div > div { height: 146px; padding: 16px 14px !important; }
          .digitalgift-shell .digitalgift-metrics > div > div > span:nth-child(2) { font-size: 26px !important; }
          .digitalgift-shell .digitalgift-metrics > div > div > span:last-child { font-weight: 500 !important; }
          .digitalgift-shell .digitalgift-metrics > div:last-child { padding-top: 8px !important; }
          .digitalgift-shell .digitalgift-stock-cta { align-items: flex-end; padding-top: 24px; }
          .digitalgift-shell .digitalgift-video-panel { grid-column: 1; grid-row: 1; order: 1; position: relative; z-index: 2; gap: 16px !important; border-radius: 14px !important; padding: 0 !important; }
          .digitalgift-shell .digitalgift-quote-panel { gap: 16px !important; padding-top: 24px !important; padding-bottom: 6px !important; margin-bottom: 0 !important; }
          .digitalgift-shell .digitalgift-quote-price { position: relative; left: -14px; width: calc(100% + 14px); justify-self: stretch; align-items: flex-start; }
          .digitalgift-shell .digitalgift-quote-logo { position: relative; right: -14px; }
          .digitalgift-shell .digitalgift-quote-company { position: relative; left: -14px; width: calc(100% + 28px); text-align: left !important; direction: ltr !important; }
          .digitalgift-shell .digitalgift-video-copy { order: 1; display: block !important; min-height: 0 !important; padding: 0 !important; }
          .digitalgift-shell .digitalgift-video-slot,
          .digitalgift-shell .digitalgift-video-panel > div:last-child { order: 2; height: auto !important; }
          .digitalgift-shell .digitalgift-video-panel .digitalgift-video { height: 179.437px !important; }
          .digitalgift-shell .digitalgift-video-poster-chart { bottom: 0 !important; height: 79.75px !important; }
          .digitalgift-shell .digitalgift-video-poster-title { top: 28.48px !important; left: auto !important; right: 10% !important; width: 54% !important; align-items: flex-start !important; text-align: right !important; gap: 4.557px !important; }
          .digitalgift-shell .digitalgift-video-poster-title > div { font-size: 7.41px !important; line-height: 1.3 !important; letter-spacing: 0.4446px !important; }
          .digitalgift-shell .digitalgift-video-poster-title > strong { font-size: 13.67px !important; line-height: 1.25 !important; }
          .digitalgift-shell .digitalgift-video-poster-title > span { font-size: 8.54px !important; line-height: 1.5 !important; }
          .digitalgift-shell .digitalgift-video-poster-play { left: 19.37px !important; top: 116.78px !important; width: 43.293px !important; height: 43.293px !important; }
          .digitalgift-shell .digitalgift-video-poster-play img { width: 15.95px !important; height: 15.95px !important; }
          .digitalgift-shell .digitalgift-video-chapters .digitalgift-video-chapter { display: none !important; }
          .digitalgift-shell .digitalgift-video-chapters .digitalgift-video-chapter:first-child { display: flex !important; min-height: 46px !important; border: 1px solid #E3E6EE !important; border-radius: 14px !important; padding: 0 14px !important; box-sizing: border-box; }
          .digitalgift-shell .digitalgift-video-chapters .digitalgift-video-chapter:first-child > span,
          .digitalgift-shell .digitalgift-video-chapters .digitalgift-video-chapter:first-child > button { font-size: 0 !important; }
          .digitalgift-shell .digitalgift-video-chapters .digitalgift-video-chapter:first-child > span::after { content: '5'; font-family: 'Heebo', sans-serif; font-size: 13px; color: #646A7E; }
          .digitalgift-shell .digitalgift-video-chapters .digitalgift-video-chapter:first-child > button::after { content: 'פרקים בסרטון'; font-family: 'Heebo', sans-serif; font-size: 15px; line-height: 1.2; color: #02031A; font-weight: 700; }
          .digitalgift-shell .digitalgift-video-links { margin-top: 16px !important; }
          .digitalgift-shell .digitalgift-video-chapters { margin-top: 16px !important; }
          .digitalgift-shell .digitalgift-video-chapters { height: 46px !important; min-height: 46px !important; max-height: 46px !important; flex: 0 0 46px !important; }
          .digitalgift-shell .digitalgift-video-chapters.is-open { height: auto !important; min-height: 0 !important; max-height: none !important; flex: 0 0 auto !important; }
          .digitalgift-shell .digitalgift-video-chapters { order: 4 !important; }
          .digitalgift-shell .digitalgift-video-links { order: 3 !important; }
          .digitalgift-shell .digitalgift-video-chapters.is-open { position: relative; z-index: 50 !important; overflow: visible !important; }
          .digitalgift-shell .digitalgift-video-chapters.is-open .digitalgift-video-chapters-list { position: relative; z-index: 51 !important; background: #FFFFFF; }
          .digitalgift-shell .digitalgift-video-chapters.is-open .digitalgift-video-chapter:first-child { min-height: 52px !important; border: 0 !important; border-bottom: 1px solid #E3E6EE !important; border-radius: 0 !important; padding: 0 !important; }
          .digitalgift-shell .digitalgift-video-chapters.is-open .digitalgift-video-chapter:first-child > span::after,
          .digitalgift-shell .digitalgift-video-chapters.is-open .digitalgift-video-chapter:first-child > button::after { content: none !important; }
          .digitalgift-shell .digitalgift-video-chapters.is-open .digitalgift-video-chapter:first-child > span { font-size: 13px !important; }
          .digitalgift-shell .digitalgift-video-chapters.is-open .digitalgift-video-chapter:first-child > button { font-size: 15px !important; }
          .digitalgift-shell .digitalgift-video-chapters-toggle { display: flex !important; align-items: center; justify-content: space-between; width: 100%; min-height: 46px; padding: 12px 14px; border: 1px solid #E3E6EE; border-radius: 14px; background: #FFFFFF; color: #02031A; direction: ltr !important; font-family: 'Heebo', sans-serif; font-size: 15px; font-weight: 700; text-align: right; cursor: pointer; box-sizing: border-box; }
          .digitalgift-shell .digitalgift-video-chapters-toggle > span:first-child { order: 1; color: #646A7E; font-family: 'Heebo', sans-serif; font-size: 13px; font-weight: 400; }
          .digitalgift-shell .digitalgift-video-chapters-toggle > span:last-child { order: 2; }
          .digitalgift-shell .digitalgift-video-chapters-list { display: none; }
          .digitalgift-shell .digitalgift-video-chapters-list.is-open { display: flex; flex-direction: column; width: 100%; }
          .digitalgift-shell .digitalgift-video-chapters-list .digitalgift-video-chapter { display: flex !important; min-height: 52px !important; border: 0 !important; border-bottom: 1px solid #E3E6EE !important; border-radius: 0 !important; padding: 0 !important; }
          .digitalgift-shell .digitalgift-video-chapters-list .digitalgift-video-chapter > span { font-size: 13px !important; }
          .digitalgift-shell .digitalgift-video-chapters-list .digitalgift-video-chapter > button { font-size: 15px !important; }
          .digitalgift-shell .digitalgift-video-links { width: 100% !important; flex-direction: row !important; flex-wrap: nowrap !important; justify-content: flex-start !important; gap: 24px !important; }
          .digitalgift-shell .digitalgift-video-links > button:nth-child(2) { display: none !important; }
          .digitalgift-shell #digitalgift-stock-chart > div:nth-child(2) button:first-child { display: none !important; }
          .digitalgift-shell #digitalgift-stock-chart > div:nth-child(2) button.is-selected { background: #02031A !important; color: #FFFFFF !important; }
          .digitalgift-shell .digitalgift-search-suggestions > button:nth-of-type(1),
          .digitalgift-shell .digitalgift-search-suggestions > button:nth-of-type(2) { display: none !important; }
          .digitalgift-shell .digitalgift-search-suggestions { justify-content: flex-end !important; }
          .digitalgift-shell .digitalgift-stock-panel > .digitalgift-search-wrap,
          .digitalgift-shell .digitalgift-stock-panel > .digitalgift-quote-panel { width: 100%; margin-left: 0 !important; }
          .digitalgift-shell .digitalgift-stock-panel > .digitalgift-search-wrap { margin-bottom: 24px !important; }
          .digitalgift-shell .digitalgift-search-dropdown { width: 100% !important; }
          .digitalgift-shell #digitalgift-legal-modal { padding: 10px !important; }
          .digitalgift-shell #digitalgift-legal-modal-panel { width: 100% !important; height: min(800px, calc(100vh - 20px)) !important; max-height: calc(100vh - 20px) !important; }
          .digitalgift-shell #legal-modal-header { padding: 18px 14px 0 !important; }
          .digitalgift-shell #legal-modal-header h2 { font-size: 24px !important; line-height: 1.1 !important; letter-spacing: 0 !important; white-space: nowrap !important; }
          .digitalgift-shell #legal-modal-content { padding: 0 !important; }
          .digitalgift-shell #legal-modal-actions { padding: 14px 14px 18px !important; }
          .digitalgift-shell #legal-modal-content iframe { border: 0 !important; border-radius: 0 !important; }
        }
        @media (min-width: 861px) and (max-width: 1200px) {
          .digitalgift-shell .digitalgift-main { grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr) !important; }
          .digitalgift-shell .digitalgift-video-panel,
          .digitalgift-shell .digitalgift-video-panel > div:last-child { min-width: 0; }
        }
        @media (min-width: 861px) {
          .digitalgift-shell .digitalgift-video-chapters { margin-top: 16px; }
          .digitalgift-shell > .digitalgift-header { display: none !important; }
          .digitalgift-shell .digitalgift-desktop-topbar { display: flex !important; }
          .digitalgift-shell .digitalgift-video-panel .digitalgift-video { width: 100% !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-testimonial { display: none !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-note { display: none !important; }
        }
        @media (max-width: 768px) {
          .digitalgift-shell .digitalgift-header,
          .digitalgift-shell .digitalgift-intro,
          .digitalgift-shell .digitalgift-layout,
          .digitalgift-shell .digitalgift-content-section { max-width: 100% !important; }
          .digitalgift-shell .digitalgift-intro { align-items: flex-start !important; gap: 16px !important; padding: 40px 20px 104px !important; text-align: right !important; }
          .digitalgift-shell .digitalgift-intro > div:first-child,
          .digitalgift-shell .digitalgift-intro > h1,
          .digitalgift-shell .digitalgift-intro > p { align-self: flex-start !important; width: 335px !important; max-width: 100%; text-align: right !important; direction: rtl !important; }
          .digitalgift-shell .digitalgift-intro > p { font-size: 21px !important; line-height: 1.6 !important; }
          .digitalgift-shell .digitalgift-intro > div:last-child { width: max-content !important; max-width: 100%; flex-direction: column !important; align-items: flex-end !important; gap: 12px !important; padding-top: 16px !important; text-align: right !important; }
          .digitalgift-shell .digitalgift-intro > div:last-child > span:nth-child(1) { order: 3; }
          .digitalgift-shell .digitalgift-intro > div:last-child > span:nth-child(2) { order: 2; }
          .digitalgift-shell .digitalgift-intro > div:last-child > span:nth-child(3) { order: 1; }
          .digitalgift-shell .digitalgift-intro > div:last-child > span { width: auto !important; justify-content: flex-start !important; }
          .digitalgift-shell .digitalgift-intro > div:last-child > span > b { flex: 0 0 32px !important; width: 32px !important; height: 32px !important; font-size: 15px !important; }
          .digitalgift-shell .digitalgift-content-section { padding: 18px 14px 0 !important; }
          .digitalgift-shell #digitalgift-content-grid { gap: 22px !important; min-height: 0 !important; }
          .digitalgift-shell .digitalgift-learn-cards { grid-template-columns: 1fr !important; }
          .digitalgift-shell #digitalgift-value-cta { width: 100% !important; margin-left: 0 !important; }
          .digitalgift-shell #digitalgift-value-cta { display: flex !important; flex-direction: column !important; width: calc(100% + 32px) !important; min-height: 0 !important; margin: 24px -16px 0 !important; padding: 0 !important; border-radius: 0 !important; background: #F6F7FB !important; direction: rtl !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-registration-body { order: 1 !important; width: 100% !important; box-sizing: border-box; padding: 32px 20px !important; gap: 20px !important; align-items: flex-end !important; background: #05051F !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter { order: 2 !important; width: 100% !important; min-height: 0 !important; height: auto !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter > img { width: 100% !important; height: auto !important; object-fit: contain !important; object-position: center top !important; }
          .digitalgift-shell #digitalgift-value-cta .digitalgift-presenter { border-radius: 0 0 20px 20px !important; background: #F6F7FB !important; }
          .digitalgift-shell #digitalgift-value-cta > a { width: 170px !important; }
          .digitalgift-shell #digitalgift-value-cta > div:nth-child(2) > div:nth-child(2) { font-size: 23px !important; }
          .digitalgift-shell #digitalgift-player-frame { max-width: 100% !important; }
        }
        @media (max-width: 768px) {
          .digitalgift-shell #digitalgift-footer { display: flex !important; width: 375px !important; max-width: 100% !important; box-sizing: border-box; margin: 72px auto 0 !important; padding: 24px 12px 96px !important; border: 0 !important; gap: 24px !important; align-items: flex-end !important; background: transparent !important; text-align: right !important; direction: rtl !important; }
          .digitalgift-shell #digitalgift-footer::before { display: none !important; }
          .digitalgift-shell #digitalgift-footer > div:nth-child(2):empty { display: none !important; }
          .digitalgift-shell .digitalgift-desktop-next-copy { display: none !important; }
          .digitalgift-shell .digitalgift-mobile-next-copy { display: block !important; }
          .digitalgift-shell .digitalgift-desktop-stage-title { display: none !important; }
          .digitalgift-shell .digitalgift-mobile-stage-title { display: block !important; }
          .digitalgift-shell .digitalgift-desktop-disclaimer { display: none !important; }
          .digitalgift-shell .digitalgift-mobile-disclaimer { display: inline !important; }
          .digitalgift-shell .digitalgift-desktop-intro-copy { display: none !important; }
          .digitalgift-shell .digitalgift-mobile-intro-copy { display: inline !important; }
          .digitalgift-shell .digitalgift-desktop-card-copy { display: none !important; }
          .digitalgift-shell .digitalgift-mobile-card-copy { display: inline !important; }
          .digitalgift-shell #digitalgift-footer-links { display: flex !important; flex-wrap: wrap !important; flex-direction: row !important; justify-content: flex-end !important; align-items: flex-start !important; gap: 8px 20px !important; width: 100% !important; direction: ltr !important; }
          .digitalgift-shell #digitalgift-mobile-menu-button { display: none !important; }
          .digitalgift-shell #digitalgift-header { position: relative; height: 64px !important; min-height: 64px !important; padding: 0 16px !important; direction: rtl; }
          .digitalgift-shell #digitalgift-header .digitalgift-header-meta { display: flex !important; position: absolute; left: 16px; right: auto; top: 14px; gap: 0; }
          .digitalgift-shell #digitalgift-header .digitalgift-header-meta > div { display: none !important; }
          .digitalgift-shell #digitalgift-header .digitalgift-header-meta > a { display: inline-flex !important; }
          .digitalgift-shell #digitalgift-header .digitalgift-header-meta > a.digitalgift-yellow-link { display: inline-flex !important; align-items: center; justify-content: center; width: 144px !important; height: 36px !important; box-sizing: border-box; padding: 0 !important; text-decoration: none !important; text-decoration-line: none !important; text-underline-offset: 0 !important; white-space: nowrap; }
          .digitalgift-shell #digitalgift-header > div:last-child { position: absolute; right: 16px !important; left: auto !important; max-width: calc(100% - 176px) !important; overflow: hidden; transform: none !important; align-items: flex-end; text-align: right !important; white-space: nowrap; }
          .digitalgift-shell #digitalgift-header > div:last-child > div { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .digitalgift-shell #digitalgift-header > div:last-child > div:first-child { font-size: 16px !important; line-height: 1 !important; font-weight: 900 !important; color: #FFFFFF !important; }
          .digitalgift-shell #digitalgift-header > div:last-child > div:last-child { margin-top: 4px; font-size: 13px !important; line-height: 1 !important; font-weight: 700 !important; color: #A7ADC2 !important; }
          .digitalgift-shell #digitalgift-mobile-workshop-strip { display: flex !important; align-items: center; justify-content: center; width: 100%; box-sizing: border-box; padding: 0 16px 12px !important; background: #02031A; color: #A7ADC2; font-family: 'Heebo', sans-serif; font-size: 13px; line-height: 1.5; text-align: center; white-space: nowrap; }
          .digitalgift-shell #digitalgift-mobile-menu { display: block !important; position: fixed !important; top: 64px !important; right: 12px !important; bottom: auto !important; left: 12px !important; width: auto !important; max-width: 380px !important; max-height: calc(100vh - 80px); margin-left: auto !important; padding: 10px 16px 16px !important; border-radius: 0 0 18px 18px !important; border: 1px solid #D8E1F0 !important; border-top: none !important; overflow-y: auto; box-shadow: 0 18px 42px rgba(15,23,42,0.24) !important; }
          .digitalgift-shell #digitalgift-mobile-menu-backdrop { display: block !important; }
          .digitalgift-shell #digitalgift-mobile-menu > div:first-child { display: block !important; padding: 0 4px 14px !important; border-bottom: 1px solid #E2E8F0 !important; }
          .digitalgift-shell #digitalgift-mobile-menu > div:nth-child(2) { padding: 14px 4px 8px !important; color: #64748B !important; }
          .digitalgift-shell #digitalgift-mobile-menu > button { display: flex !important; align-items: center; justify-content: space-between; width: 100% !important; min-height: 48px; margin: 6px 0; padding: 0 14px !important; border: 1px solid #E2E8F0 !important; border-radius: 12px !important; background: #F8FAFC !important; color: #1E3A8A !important; }
          .digitalgift-shell #digitalgift-mobile-menu > button::after { content: '‹'; color: #94A3B8; font-size: 22px; line-height: 1; }
        }
        @media (max-width: 520px) {
          .digitalgift-shell .digitalgift-content-section > div:first-child { grid-template-columns: 1fr !important; gap: 18px !important; }
          .digitalgift-shell #digitalgift-content-grid { min-height: 0 !important; }
          .digitalgift-shell #digitalgift-content-grid > div:first-child { grid-column: 1 !important; grid-row: 2 !important; text-align: right !important; }
          .digitalgift-shell #digitalgift-content-grid > div:last-child { grid-column: 1 !important; grid-row: 1 !important; text-align: right !important; }
          .digitalgift-shell #digitalgift-content-grid > div:first-child p { font-size: 17px !important; line-height: 1.6 !important; }
          .digitalgift-shell #digitalgift-content-grid > div:last-child h2 { font-size: 30px !important; line-height: 1.3 !important; text-align: right !important; direction: rtl !important; }
          .digitalgift-shell .digitalgift-floating-video { width: calc(100vw - 24px) !important; height: min(56vw, 240px) !important; }
          .digitalgift-shell #digitalgift-footer { flex-direction: column !important; align-items: flex-end !important; gap: 24px !important; position: relative; z-index: 2; }
          .digitalgift-shell #digitalgift-footer > div:first-child { min-height: 18px; width: 100%; }
          .digitalgift-shell #digitalgift-footer-links { display: flex !important; flex-direction: row !important; flex-wrap: wrap !important; align-items: flex-start !important; justify-content: flex-end !important; width: 100% !important; gap: 8px 20px !important; direction: ltr !important; }
          .digitalgift-shell #digitalgift-footer-links button { display: block !important; width: auto !important; min-height: 0; white-space: nowrap; }
          .digitalgift-shell > button[aria-label="Open accessibility options"] { bottom: 60px !important; }
          .digitalgift-shell .digitalgift-video { height: 240px !important; }
        }
        @media (max-width: 420px) {
          .digitalgift-shell .digitalgift-workshop-overlay { gap: 8px; padding: 8px 10px; min-height: 52px; }
          .digitalgift-shell .digitalgift-workshop-overlay-copy strong { font-size: 14px; }
          .digitalgift-shell .digitalgift-workshop-overlay-copy span { font-size: 11px; }
          .digitalgift-shell .digitalgift-workshop-overlay-cta { padding: 7px 12px; font-size: 14px; }
        }
      `}</style>
      <style>{`
        :focus-visible { outline: 3px solid #F59E0B !important; outline-offset: 3px; }
        ${a11yUnderlineLinks ? 'a:not(.digitalgift-yellow-link), [data-a11y-link="true"] { text-decoration: underline !important; text-decoration-thickness: 2px !important; text-underline-offset: 3px !important; box-shadow: inset 0 -3px 0 rgba(245,158,11,0.28) !important; }' : ''}
        ${a11yHighlightHeadings ? 'h1, h2, h3, h4, [data-a11y-heading="true"] { outline: 3px solid #F59E0B !important; outline-offset: 4px !important; background: #FFFBEB !important; color: #111827 !important; box-shadow: 0 3px 0 #F59E0B !important; }' : ''}
        ${a11yHideImages ? 'img, canvas { visibility: hidden !important; }' : ''}
        ${a11yMarkImages ? 'img, canvas { outline: 2px solid #F59E0B !important; outline-offset: 3px; }' : ''}
        ${a11yStopAnimations || a11yReduceMotion ? '*, *::before, *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; scroll-behavior: auto !important; }' : ''}
      `}</style>
      <header id="digitalgift-header" className="digitalgift-header" style={{ maxWidth: 'none', margin: 0, padding: '0 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24, minHeight: 64, boxSizing: 'border-box' }}>
        <button
          id="digitalgift-mobile-menu-button"
          type="button"
          aria-label="Open menu"
          aria-expanded={showMobileMenu}
          onClick={() => setShowMobileMenu((value) => !value)}
          style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, flex: '0 0 38px', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 10, background: 'rgba(255,255,255,0.08)', color: '#FFFFFF', cursor: 'pointer', fontSize: 22, lineHeight: 1 }}
        >
          <span aria-hidden="true">☰</span>
        </button>
        <div className="digitalgift-header-meta">
          <a className="digitalgift-yellow-link" href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026" target="_blank" rel="noreferrer" style={{ color: '#FFFFFF', fontFamily: 'Heebo, sans-serif', fontSize: 15, lineHeight: 1.5, fontWeight: 700, textDecoration: 'none', border: '2px solid #FFFFFF', borderRadius: 10, padding: '7px 16px' }}>לפרטים והרשמה</a>
          <div dir="rtl" style={{ textAlign: 'right' }}>סדנת השקעות ערך · שני ערבים בזום · ללא עלות</div>
        </div>
        <div dir="rtl" style={{ textAlign: 'right', fontFamily: 'Heebo, sans-serif' }}>
          <div className="digitalgift-brand-name" style={{ fontSize: 18, lineHeight: 1, fontWeight: 900 }}>אלון הזה</div>
          <div className="digitalgift-brand-subtitle" style={{ fontSize: 13, lineHeight: 1.2, fontWeight: 700, color: '#A7ADC2' }}>GARP Value Investing</div>
        </div>
      </header>
      <header className="digitalgift-desktop-topbar" dir="rtl">
        <div dir="rtl" style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', textAlign: 'right', fontFamily: 'Heebo, sans-serif', color: '#FFFFFF' }}>
          <span className="digitalgift-brand-name" style={{ fontSize: 18, lineHeight: 1, fontWeight: 900, color: '#FFFFFF' }}>אלון הזה</span>
          <span className="digitalgift-brand-separator" aria-hidden="true">·</span>
          <span className="digitalgift-brand-subtitle" style={{ fontSize: 18, lineHeight: 1, fontWeight: 900, color: '#FFFFFF' }} dir="ltr">GARP <span className="digitalgift-brand-separator" aria-hidden="true">·</span> Value Investing</span>
        </div>
        <div className="digitalgift-header-meta">
          <div dir="rtl" style={{ textAlign: 'right' }}>סדנת השקעות ערך · שני ערבים בזום · ללא עלות</div>
          <a className="digitalgift-yellow-link" href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>לפרטים והרשמה</a>
        </div>
      </header>
      <div id="digitalgift-mobile-workshop-strip" dir="rtl">סדנת השקעות ערך · שני ערבים בזום · ללא עלות</div>
      {showMobileMenu && (
        <button
          id="digitalgift-mobile-menu-backdrop"
          type="button"
          aria-label="Close menu"
          onClick={() => setShowMobileMenu(false)}
          style={{ display: 'none', position: 'fixed', inset: 0, zIndex: 1090, border: 'none', background: 'rgba(15,23,42,0.42)', cursor: 'default' }}
        />
      )}
      {showMobileMenu && (
        <div
          id="digitalgift-mobile-menu"
          dir="rtl"
          role="menu"
          style={{ display: 'none', position: 'absolute', top: 68, right: 12, width: 'min(380px, calc(100vw - 24px))', zIndex: 1100, padding: '10px 8px 8px', background: '#FFFFFF', border: '1px solid #D8E1F0', borderRadius: 16, boxShadow: '0 18px 42px rgba(15,23,42,0.24)' }}
        >
          <div style={{ padding: '0 4px 14px', borderBottom: '1px solid #EEF2F7', color: '#0F172A', fontSize: 15, fontWeight: 800 }}>
            <div aria-hidden="true" style={{ width: 38, height: 4, margin: '0 auto 14px', borderRadius: 999, background: '#CBD5E1' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>תפריט</span>
              <button type="button" onClick={() => setShowMobileMenu(false)} aria-label="Close menu" style={{ width: 30, height: 30, border: 'none', borderRadius: 8, background: '#F1F5F9', color: '#475569', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
          </div>
          <div style={{ padding: '10px 10px 5px', color: '#64748B', fontSize: 11, fontWeight: 800 }}>מידע ועזרה</div>
          {[
            ['/waiting-page-terms.html', 'תנאי שימוש'],
            ['/privacy-policy.html', 'מדיניות פרטיות'],
            ['/accessibility-statement.html', 'הצהרת נגישות'],
            ['/investment-disclaimer.html', 'הצהרת השקעות'],
          ].map(([url, title]) => (
            <button
              key={url}
              type="button"
              onClick={() => {
                setShowMobileMenu(false);
                openLegalModal(url, title);
              }}
              role="menuitem"
              style={{ display: 'block', width: '100%', padding: '11px 10px', border: 'none', borderRadius: 9, background: 'transparent', color: '#1E3A8A', textAlign: 'right', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              {title}
            </button>
          ))}
        </div>
      )}
      <section id="digitalgift-intro" className="digitalgift-intro" dir="rtl" style={{ textAlign: 'right', boxSizing: 'border-box' }}>
        <div style={{ color: '#7FA4FF', fontSize: 14, lineHeight: 1.3, letterSpacing: 1.12, fontWeight: 700 }}>המתנה הדיגיטלית שלך</div>
        <h1 data-a11y-heading="true" style={{ margin: 0, width: 'min(900px, 100%)', fontSize: 'clamp(34px, 3.6vw, 52px)', lineHeight: 1.15, fontWeight: 900 }}>6 הפרמטרים לזיהוי מניה מעולה</h1>
        <p style={{ margin: '16px 0 0', width: 'min(620px, 100%)', fontSize: 'clamp(16px, 1.45vw, 21px)', lineHeight: 1.6, color: '#A7ADC2' }}>
          <span className="digitalgift-desktop-intro-copy">התנסו במערכת: בחרו מניה שתרצו לחקור והפעילו את סרטון ההסבר. אפשר לצפות ולתרגל במקביל, באותו מסך.</span>
          <span className="digitalgift-mobile-intro-copy">התנסו במערכת: בחרו מניה שתרצו<br />לחקור והפעילו את סרטון ההסבר.<br />אפשר לצפות ולתרגל במקביל, באותו<br />מסך.</span>
        </p>
        <div style={{ marginTop: 16, paddingTop: 16, display: 'flex', justifyContent: 'flex-start', gap: 32, flexWrap: 'wrap', fontSize: 17, lineHeight: 1.6 }}>
          <span><b style={{ display: 'inline-grid', placeItems: 'center', width: 32, height: 32, marginLeft: 12, borderRadius: 999, background: '#2468FF', color: '#FFFFFF', fontSize: 15 }}>3</b><a href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026" target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>ממשיכים לסדנה החינמית</a></span>
          <span><b style={{ display: 'inline-grid', placeItems: 'center', width: 32, height: 32, marginLeft: 12, borderRadius: 999, background: '#2468FF', color: '#FFFFFF', fontSize: 15 }}>2</b>בוחנים מניה לפי 6 הפרמטרים</span>
          <span><b style={{ display: 'inline-grid', placeItems: 'center', width: 32, height: 32, marginLeft: 12, borderRadius: 999, background: '#2468FF', color: '#FFFFFF', fontSize: 15 }}>1</b>מפעילים את הסרטון</span>
        </div>
      </section>
      <a href="#digitalgift-main" style={{ position: 'absolute', left: -10000, top: 8, zIndex: 1300, background: '#FFFFFF', color: '#111827', padding: '10px 14px', borderRadius: 8, fontWeight: 800 }} onFocus={(event) => { event.currentTarget.style.left = '16px'; }} onBlur={(event) => { event.currentTarget.style.left = '-10000px'; }}>Skip to main content</a>
      <main id="digitalgift-main" aria-labelledby="digitalgift-title" className={`digitalgift-layout digitalgift-main${isVideoFloating ? ' is-video-floating' : ''}`} style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: 20 }}>
        <section id="digitalgift-stock-panel" aria-labelledby="digitalgift-title" className="digitalgift-panel digitalgift-stock-panel" style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 16 }}>
          <h1 id="digitalgift-title" data-a11y-heading="true" className="digitalgift-heading" style={{ margin: '0 0 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 22, lineHeight: 1.2, color: '#111827', fontWeight: 700 }}>
            <span>בחנו מניה</span>
            {!isQuotaLoading && (
              <span aria-live="polite" style={{ fontSize: 15, fontWeight: quotaStatus.remaining <= 10 ? 800 : 500, color: quotaStatus.remaining <= 10 ? '#B91C1C' : '#4B5563', whiteSpace: 'nowrap' }}>
                נותרו {quotaStatus.remaining} חיפושים
              </span>
            )}
          </h1>

          <div id="digitalgift-search-section" ref={searchWrapRef} className="digitalgift-search-wrap" style={{ position: 'relative', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <label htmlFor="search-input" style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>חיפוש מניה</label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 12, rowGap: 6 }}>
              <div style={{ position: 'relative' }}>
                <img className="digitalgift-search-icon" src={digitalGiftSearchIcon} alt="" aria-hidden="true" />
                <input
                  id="search-input"
                  value={query}
                  onFocus={() => {
                    if (isSearchBlocked) {
                      openQuotaReminder(0);
                      return;
                    }
                    if (selectedSymbol && query) {
                      setQuery('');
                      setSearchError('');
                    }
                  }}
                  onClick={() => {
                    if (isSearchBlocked) openQuotaReminder(0);
                  }}
                  onKeyDown={(event) => {
                    if (isSearchBlocked && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      openQuotaReminder(0);
                    }
                  }}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSearchError('');
                  }}
                  placeholder={isSearchBlocked ? 'מכסת החיפושים נוצלה' : 'חפשו מניה, למשל GOOG'}
                  aria-label="Search by symbol or company"
                  aria-autocomplete="list"
                  aria-controls="stock-search-results"
                  aria-expanded={showSearchDropdown}
                  readOnly={isSearchBlocked}
                  style={{ width: 310, maxWidth: '100%', height: 40, boxSizing: 'border-box', borderRadius: 8, border: '1px solid #D1D5DB', padding: '0 12px 0 36px', fontSize: 14, color: '#4B5563', direction: 'rtl', textAlign: 'right' }}
                />
              </div>
              <div className="digitalgift-search-suggestions" dir="rtl">
                <span>לא בטוחים? נסו:</span>
                {['COST', 'INTC', 'KO', 'MSFT', 'GOOG'].map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => {
                      const stock = localStocks.find((row) => row.symbol === suggestion);
                      if (!stock) {
                        setSearchError('Stock data is unavailable for this symbol.');
                        return;
                      }
                      void onSelectResult({
                        symbol: stock.symbol,
                        name: stock.name,
                        currency: 'USD',
                        stockExchange: stock.exchange,
                        exchangeShortName: stock.exchange,
                        exchange: stock.exchange,
                      });
                    }}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
            {showSearchDropdown && (
              <div id="stock-search-results" className="digitalgift-search-dropdown" role="listbox" aria-label="Stock search results" style={{ position: 'absolute', top: 46, left: 0, right: 0, background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 20px rgba(0,0,0,0.08)', zIndex: 10, maxHeight: 260, overflowY: 'auto' }}>
                {isSearching ? (
                  <div style={{ padding: 10, fontSize: 13, color: '#6B7280' }}>Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div style={{ padding: 10, fontSize: 13, color: '#6B7280' }}>No results</div>
                ) : searchResults.map((row) => (
                  <button
                    key={`${row.symbol}-${row.exchangeShortName}`}
                    type="button"
                    role="option"
                    aria-label={`${row.symbol}, ${row.name}, ${getSearchResultExchange(row)}`}
                    onClick={() => onSelectResult(row)}
                    style={{ width: '100%', textAlign: 'left', border: 'none', background: '#FFFFFF', cursor: 'pointer', padding: '10px 12px', borderBottom: '1px solid #F3F4F6' }}
                  >
                    <div style={{ fontWeight: 700, color: '#111827' }}>{row.symbol} · {row.name}</div>
                    <div style={{ fontSize: 12, color: '#6B7280' }}>{getSearchResultExchange(row)}</div>
                  </button>
                ))}
              </div>
            )}

            <div style={{ marginTop: 8 }}>
              {isQuotaLoading ? (
                <div style={{ fontSize: 12, color: '#6B7280' }}>Checking free search quota...</div>
              ) : null}

              {quotaError && (
                <div style={{ fontSize: 12, color: '#B91C1C', marginTop: 6 }}>{quotaError}</div>
              )}
              {searchError && (
                <div role="alert" style={{ fontSize: 12, color: '#B91C1C', marginTop: 6 }}>{searchError}</div>
              )}
            </div>
          </div>

          <div id="digitalgift-quote-panel" className="digitalgift-panel digitalgift-quote-panel" style={{ border: '1px solid #E5E7EB', borderRadius: 10, padding: 12, marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="digitalgift-quote-company" dir="rtl">
              <div style={{ fontWeight: 800, fontSize: 13, color: '#111827' }}>{quote?.name || symbolTitle}</div>
              <div style={{ fontSize: 12, color: '#6B7280' }}>{symbolSubtitle}</div>
            </div>
            <div className="digitalgift-quote-price" dir="ltr">
              {asUsd(quote?.price ?? null)}
              {selectedSymbol ? (
                <small style={{ color: (quote?.changesPercentage ?? 1.2) < 0 ? '#D92D20' : '#0E8A5F' }}>
                  {quote?.changesPercentage != null ? `${quote.changesPercentage >= 0 ? '+' : ''}${quote.changesPercentage.toFixed(2)}%` : '+1.20%'}
                </small>
              ) : null}
            </div>
            {selectedSymbol ? (
              <div
                className="digitalgift-quote-logo"
                aria-label={`${selectedSymbol} logo`}
                style={{ background: 'transparent' }}
              >
                {isQuoteLogoFailed ? (
                  selectedSymbol.charAt(0)
                ) : (
                  <img
                    src={`https://financialmodelingprep.com/image-stock/${selectedSymbol}.png`}
                    alt={`${selectedSymbol} logo`}
                    onError={() => setIsQuoteLogoFailed(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 12 }}
                  />
                )}
              </div>
            ) : null}
          </div>

          <div id="digitalgift-metrics" className="digitalgift-metrics" style={{ maxWidth: 780, margin: '8px auto 16px', paddingTop: 12, borderTop: '1px solid #B8C8E2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ display: 'grid', gap: 8 }}>
              <MetricRow label="5-Year Revenue Growth" value={metricState.revenueGrowth5y} criterion="Threshold: ≥ 10%" threshold={10} />
              <MetricRow label="5-Year Earnings Growth" value={metricState.earningsGrowth5y} criterion="Threshold: ≥ 10%" threshold={10} />
              <MetricRow label="Net Profit Margin" value={metricState.netProfitMargin} criterion="Threshold: ≥ 10%" threshold={10} />
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              <MetricRow label="ROIC" value={metricState.roic} criterion="ROIC >= 15%" threshold={15} />
              <MetricRow label="Cash To Debt Ratio" value={metricState.cashToDebt} criterion="Threshold: ≥ 1" threshold={1} />
              <MetricRow label="Years Of Profitability" value={metricState.yearsOfProfitability} criterion="Threshold: ≥ 7 (out of 10 years)" threshold={7} />
            </div>
            <div dir="rtl" style={{ gridColumn: '1 / -1', paddingTop: 12, color: '#8A8F98', fontSize: 13, lineHeight: 1.5, textAlign: 'center' }}>
              עמידה בתנאי המינימום היא נקודת פתיחה למחקר. היא אינה מעידה שהמניה מתאימה להשקעה במחירה הנוכחי.
            </div>
          </div>
          <div className="digitalgift-stock-cta">
            <p dir="rtl">
              רוצים ללמוד מה עושים עם המספרים האלה?
              זה בדיוק מה שעושים בסדנה.
            </p>
            <a href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026" target="_blank" rel="noreferrer" dir="rtl">לפרטי הסדנה ←</a>
          </div>

          <section id="digitalgift-stock-chart" aria-labelledby="stock-price-title" className="digitalgift-panel" style={{ width: '100%', maxWidth: 660, boxSizing: 'border-box', margin: '0 auto', border: '1px solid #E5E7EB', borderRadius: 10, padding: 12 }}>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
              <h2 id="stock-price-title" data-a11y-heading="true" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>Stock Price</h2>
              {selectedSymbol ? (
                <div className="digitalgift-stock-price-stats">
                  <span className="digitalgift-stock-price-return" style={{ color: topStats.rangeReturn != null && topStats.rangeReturn < 0 ? '#DC2626' : '#059669', fontWeight: 700 }}>{selectedRange}: {asPct(topStats.rangeReturn)}</span>
                  <div className="digitalgift-stock-price-levels">
                    <span style={{ color: topStats.aboveLow != null && topStats.aboveLow < 0 ? '#DC2626' : '#059669', fontWeight: 700 }}>Above Low: {asPct(topStats.aboveLow)}</span>
                    <span style={{ color: topStats.belowHigh != null && topStats.belowHigh >= 0 ? '#059669' : '#DC2626', fontWeight: 700 }}>Below High: {asPct(topStats.belowHigh)}</span>
                  </div>
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {RANGE_ORDER.map((rk) => (
                <button
                  key={rk}
                  type="button"
                  className={`digitalgift-range-button${rk === 'ALL' ? ' digitalgift-range-all' : ''}${selectedRange === rk ? ' is-selected' : ''}`}
                  onClick={() => setSelectedRange(rk)}
                  style={{
                    border: selectedRange === rk ? '1px solid #2F58F2' : '1px solid #E5E7EB',
                    background: selectedRange === rk ? '#2F58F2' : '#FFFFFF',
                    color: selectedRange === rk ? '#FFFFFF' : '#374151',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontWeight: 700,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {rk}
                </button>
              ))}
            </div>

            <div id="digitalgift-chart-frame" style={{ height: 220 }}>
              {isLoading ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>Loading chart...</div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </section>

        </section>

        <div className="digitalgift-sheet-divider" aria-hidden="true" />

        <section id="digitalgift-video-panel" aria-label="Video player" className={`digitalgift-panel digitalgift-video-panel${isVideoFloating ? ' is-video-floating' : ''}`} style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div id="digitalgift-video-copy" className="digitalgift-video-copy" dir="rtl" style={{ minHeight: 330, padding: '24px 24px', boxSizing: 'border-box', textAlign: 'right' }}>
            <div style={{ fontSize: 13, color: '#2F58F2', marginBottom: 8, textAlign: 'right', fontWeight: 800 }}>סרטון ההסבר</div>
            <h2 data-a11y-heading="true" style={{ margin: 0, fontSize: 17, lineHeight: 1.45, fontWeight: 800, color: '#111827', textAlign: 'right' }}>
              איך קוראים את הנתונים
              <br />
              ומה בודקים מעבר להם?
            </h2>
            <p style={{ margin: '12px 0 0', fontSize: 13, lineHeight: 1.7, color: '#374151', textAlign: 'right' }}>
              כ‑20 דקות · הדגמה של 6 הפרמטרים על שתי מניות
            </p>
            <p style={{ margin: '12px 0 0', fontSize: 13, lineHeight: 1.7, color: '#374151', textAlign: 'right' }}>
              לאחר הצפייה, נסו את הכלי על מניה שמעניינת אתכם.
            </p>
            <a href="/guide.pdf" target="_blank" rel="noreferrer" className="digitalgift-link" style={{ display: 'block', width: 'fit-content', margin: '10px 0 0 auto', fontSize: 13, fontWeight: 800, textDecoration: 'underline' }}>הורדת מדריך PDF</a>
          </div>
          <div ref={playerSlotRef} className={`digitalgift-video-slot${showVideoChapters ? ' is-chapters-open' : ''}${isVideoFloating ? ' is-video-floating' : ''}`} style={{ width: '100%', height: 360 }}>
            <div
              id="digitalgift-player-frame"
              ref={playerPreviewRef}
              className={isVideoFloating ? 'digitalgift-floating-video' : undefined}
              style={{
                position: isVideoFloating ? 'fixed' : 'relative',
                left: isVideoFloating ? floatingVideoPosition.left : undefined,
                top: isVideoFloating ? floatingVideoPosition.top : undefined,
                width: isVideoFloating ? floatingVideoPosition.width : '100%',
                height: isVideoFloating ? floatingVideoPosition.height : undefined,
                zIndex: isVideoFloating ? 2147483647 : undefined,
                pointerEvents: isVideoFloating ? 'auto' : undefined,
                resize: isVideoFloating ? 'both' : undefined,
                borderRadius: 12,
                overflow: 'hidden',
                border: '1px solid #E5E7EB',
                background: '#000',
              }}
            >
            {isVideoFloating && (
              <div
                onPointerDown={beginFloatingVideoDrag}
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, minHeight: 34, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, padding: '0 8px', boxSizing: 'border-box', background: 'rgba(17,24,39,0.92)', color: '#FFFFFF', cursor: 'move', fontSize: 11, touchAction: 'none', userSelect: 'none' }}
              >
                <span>נגן צף · גרור למיקום הרצוי</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <button
                      type="button"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={toggleVideoPlayback}
                      style={{ border: 0, borderRadius: 4, padding: '3px 6px', background: 'rgba(255,255,255,0.14)', color: '#FFFFFF', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}
                      aria-label={isVideoPaused ? 'הפעלת הסרטון' : 'השהיית הסרטון'}
                      title={isVideoPaused ? 'הפעלת הסרטון' : 'השהיית הסרטון'}
                    >
                      {isVideoPaused ? '▶' : '⏸'}
                    </button>
                    <button
                      type="button"
                      onPointerDown={(event) => event.stopPropagation()}
                      onClick={() => { void expandVideo(); }}
                      style={{ border: 0, borderRadius: 4, padding: '3px 6px', background: 'rgba(255,255,255,0.14)', color: '#FFFFFF', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                      aria-label="הגדלת הסרטון"
                      title="הגדלת הסרטון"
                    >
                      ⛶
                    </button>
                  <button
                    type="button"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={() => setIsVideoFloating(false)}
                    style={{ border: 0, background: 'transparent', color: '#FFFFFF', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
                    aria-label="Close floating player"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            {youtubeEmbedSrc ? (
              isYoutubeStarted ? (
                <>
                  <iframe
                    id="digitalgift-youtube-player"
                    ref={youtubeIframeRef}
                    title="DigitalGift investment video"
                    className="digitalgift-video digitalgift-video-poster"
                    src={`${youtubeEmbedSrc}${youtubeEmbedSrc.includes('?') ? '&' : '?'}autoplay=1&controls=1&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{ width: '100%', height: 360, display: 'block', border: 0 }}
                  />
                </>
              ) : (
                <div
                  className="digitalgift-video"
                  role="button"
                  tabIndex={0}
                  aria-label="Play investment video"
                  onClick={() => setIsYoutubeStarted(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setIsYoutubeStarted(true);
                    }
                  }}
                  style={{ width: '100%', height: 360, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18, background: '#0B1024', color: '#FFFFFF', cursor: 'pointer' }}
                >
                  <img className="digitalgift-video-poster-grid" src="/images/digitalgift-video-grid.svg" alt="" aria-hidden="true" />
                  <img className="digitalgift-video-poster-chart" src="/images/digitalgift-video-chart-line.svg" alt="" aria-hidden="true" />
                  <div className="digitalgift-video-poster-topline" style={{ position: 'absolute', top: 16, left: 18, right: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#F3F4F6' }}>
                    <span>שתי חברות · שישה פרמטרים</span>
                    <span dir="rtl">הדגמה עם אלון הזה</span>
                  </div>
                  <div className="digitalgift-video-poster-title" dir="rtl">
                    <div>סרטון ההסבר</div>
                    <strong>איך קוראים את הנתונים<br />ומה בודקים מעבר להם?</strong>
                    <span>6 פרמטרים · 2 מניות · כ‑20 דקות</span>
                  </div>
                  <div className="digitalgift-video-poster-caption" dir="rtl" style={{ position: 'absolute', right: 18, bottom: 52, fontSize: 12, color: '#F3F4F6' }}>צפו בהסבר ובחינת שתי חברות</div>
                  <div className="digitalgift-video-poster-play"><img src="/images/digitalgift-video-play.svg" alt="" aria-hidden="true" /></div>
                  <div className="digitalgift-video-poster-controls" aria-hidden="true" style={{ position: 'absolute', left: 14, right: 14, bottom: 10, color: '#D1D5DB', fontSize: 12 }}>
                    <div style={{ height: 3, background: '#6B7280', marginBottom: 8 }}>
                      <div style={{ width: 0, height: 3, background: '#FFFFFF' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 16 }}>▶</span>
                      <span>0:00</span>
                      <button
                        type="button"
                        aria-label={isPreviewMuted ? 'Unmute' : 'Mute'}
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsPreviewMuted((muted) => !muted);
                        }}
                        style={{ marginLeft: 'auto', display: 'grid', placeItems: 'center', border: 0, padding: 0, background: 'transparent', color: '#D1D5DB', cursor: 'pointer' }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" style={{ width: 16, height: 16 }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 10v4h3l4 3V7l-4 3H5z" />
                        {!isPreviewMuted && <path strokeLinecap="round" d="M16 9.5a4 4 0 010 5M18.5 7a7.5 7.5 0 010 10" />}
                        {isPreviewMuted && <path strokeLinecap="round" d="M16 10l4 4m0-4l-4 4" />}
                        </svg>
                      </button>
                      <button
                        type="button"
                        aria-label="Fullscreen"
                        onClick={(event) => {
                          event.stopPropagation();
                          void playerPreviewRef.current?.requestFullscreen?.();
                        }}
                        style={{ display: 'grid', placeItems: 'center', border: 0, padding: 0, background: 'transparent', color: '#D1D5DB', cursor: 'pointer', fontSize: 16 }}
                      >
                        ⛶
                      </button>
                      <button
                        type="button"
                        aria-label="Player menu"
                        onClick={(event) => {
                          event.stopPropagation();
                          setShowPreviewMenu((visible) => !visible);
                        }}
                        style={{ display: 'grid', placeItems: 'center', border: 0, padding: 0, background: 'transparent', color: '#D1D5DB', cursor: 'pointer', fontSize: 16 }}
                      >
                        ⋮
                      </button>
                    </div>
                  </div>
                  {showPreviewMenu && (
                    <div role="status" style={{ position: 'absolute', right: 14, bottom: 38, background: '#111827', color: '#FFFFFF', padding: '6px 9px', borderRadius: 4, fontSize: 11 }}>
                      Player options
                    </div>
                  )}
                </div>
              )
            ) : (
              <video
                ref={videoRef}
                controls
                className="digitalgift-video"
                style={{ width: '100%', height: 360, display: 'block' }}
                src={effectiveVideoSrc || undefined}
                onLoadedMetadata={(event) => {
                  setVideoPlayback({
                    currentTime: event.currentTarget.currentTime,
                    duration: Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0,
                  });
                }}
                onTimeUpdate={(event) => {
                  setVideoPlayback({
                    currentTime: event.currentTarget.currentTime,
                    duration: Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0,
                  });
                }}
                onPlay={() => setIsVideoPaused(false)}
                onPause={() => setIsVideoPaused(true)}
              >
                Your browser does not support the video tag.
              </video>
            )}
            {workshopMarkerPositions.length > 0 && (
              <div className="digitalgift-workshop-markers" aria-hidden="true">
                {workshopMarkerPositions.map(({ time, left }) => (
                  <span key={time} style={{ left }} />
                ))}
              </div>
            )}
            {showWorkshopCta && (
              <div className="digitalgift-workshop-overlay" role="region" aria-label="סדנת השקעות ערך בזום">
                <div className="digitalgift-workshop-overlay-copy">
                  <strong>סדנת השקעות ערך בזום</strong>
                  <span>שני ערבים · ללא עלות</span>
                </div>
                <a
                  className="digitalgift-workshop-overlay-cta"
                  href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026?utm_medium=video_overlay"
                  target="_blank"
                  rel="noreferrer"
                >
                  להרשמה
                </a>
                <button
                  type="button"
                  className="digitalgift-workshop-overlay-close"
                  aria-label="סגירת הודעת ההרשמה"
                  onClick={() => {
                    setDismissedWorkshopCtaWindows((dismissed) => new Set(dismissed).add(activeWorkshopCtaWindow));
                  }}
                >
                  ×
                </button>
              </div>
            )}
          </div>

          <div className="digitalgift-video-links" dir="rtl">
            <button type="button" onClick={() => setIsVideoFloating(true)}>
              <span>פתיחה בחלון צף</span>
              <img src={digitalGiftFloatingIcon} alt="" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => { void expandVideo(); }}>
              <span>הגדלת הסרטון</span>
              <img src={digitalGiftExpandIcon} alt="" aria-hidden="true" />
            </button>
            <a href="/guide.pdf" target="_blank" rel="noreferrer" dir="rtl">מדריך ה‑<bdi dir="ltr">PDF</bdi></a>
          </div>
          <div className={`digitalgift-video-chapters${showVideoChapters ? ' is-open' : ''}`} dir="rtl">
            <button
              type="button"
              className="digitalgift-video-chapters-toggle"
              aria-expanded={showVideoChapters}
              onClick={() => setShowVideoChapters((isOpen) => !isOpen)}
            >
              <span>5</span>
              <span>פרקים בסרטון</span>
            </button>
            <div className={`digitalgift-video-chapters-list${showVideoChapters ? ' is-open' : ''}`}>
              {[
                ['0:00', 'מה העמוד הזה נותן לכם'],
                ['2:00', 'הדגמה ראשונה: 6 הפרמטרים על מניה אחת'],
                ['9:30', 'הזמנה לסדנה החינמית'],
                ['10:30', 'הדגמה שנייה: מה בודקים מעבר למספרים'],
                ['18:30', 'סיכום והצעד הבא'],
              ].map(([time, title]) => (
                <div className="digitalgift-video-chapter" key={`${time}-${title}`}>
                  <span>{time}</span>
                  <button type="button" onClick={() => { void playerPreviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>{title}</button>
                </div>
              ))}
            </div>
          </div>
          </div>

          {!youtubeEmbedSrc && (
            <button
              type="button"
              onClick={() => {
                void videoRef.current?.play();
              }}
              className="digitalgift-primary"
              style={{
                width: 140,
                height: 40,
                border: 'none',
                borderRadius: 10,
                background: '#111827',
                color: '#FFFFFF',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Play
            </button>
          )}
        </section>
      </main>

      <section id="digitalgift-content-section" className="digitalgift-content-section" dir="rtl" style={{ maxWidth: 1184, margin: '48px auto 0', padding: '72px 72px 0', boxSizing: 'border-box' }}>
        <div id="digitalgift-content-grid" style={{ display: 'grid', gridTemplateColumns: '1.08fr 0.92fr', gap: 42, alignItems: 'center', minHeight: 290 }}>
          <div style={{ order: 2, textAlign: 'right', color: '#111827' }}>
            <p className="digitalgift-desktop-next-copy" style={{ margin: 0, fontSize: 17, lineHeight: 1.6 }}>
              הפרמטרים אומרים לכם אם שווה להמשיך לחקור. בסדנה החינמית אלון מראה מה עושים מכאן: איך מבינים את העסק, ואיך מחליטים אם המניה שווה השקעה ובאיזה מחיר.
            </p>
            <p className="digitalgift-mobile-next-copy" style={{ margin: 0, fontSize: 17, lineHeight: 1.6 }}>
              הפרמטרים אומרים לכם אם שווה להמשיך לחקור. בסדנה החינמית אלון מראה מה עושים מכאן.
            </p>
          </div>
          <div style={{ order: 1, textAlign: 'right' }}>
            <div className="digitalgift-next-eyebrow">הצעד הבא</div>
            <h2 dir="rtl" data-a11y-heading="true" style={{ margin: 0, textAlign: 'right', direction: 'rtl', fontSize: 'clamp(32px, 4vw, 46px)', lineHeight: 1.35, fontWeight: 800, color: '#111827' }}>
              מצאתם מניה שעוברת את 6 הפרמטרים?
              זה רק השלב הראשון.
            </h2>
          </div>
        </div>
        <div className="digitalgift-learn-cards" dir="rtl" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 24 }}>
          <div style={{ padding: '18px 20px', borderRadius: 14, background: '#F0F3F8', color: '#111827', fontSize: 13, lineHeight: 1.7 }}>
            <div className="digitalgift-learn-card-title" style={{ color: '#667085', fontWeight: 800 }}>
              <span className="digitalgift-desktop-stage-title">מה עשיתם בעמוד הזה</span>
              <span className="digitalgift-mobile-stage-title">מה עשיתם בעמוד הזה</span>
            </div>
            <div className="digitalgift-learn-card-row">
              <span className="digitalgift-learn-card-check" aria-hidden="true">✓</span>
              <span>בחנתם מניה לפי 6 פרמטרים</span>
            </div>
            <div className="digitalgift-learn-card-row">
              <span className="digitalgift-learn-card-check" aria-hidden="true">✓</span>
              <span>
                <span className="digitalgift-desktop-card-copy">ראיתם תוך רגע אם שווה להמשיך לחקור</span>
                <span className="digitalgift-mobile-card-copy">ראיתם תוך רגע אם שווה להמשיך לחקור</span>
              </span>
            </div>
          </div>
          <div style={{ padding: '18px 20px', borderRadius: 14, background: '#E8EEFF', color: '#111827', fontSize: 13, lineHeight: 1.7 }}>
            <div className="digitalgift-learn-card-title" style={{ color: '#2468FF', fontWeight: 800 }}>מה לומדים בסדנה</div>
            <div className="digitalgift-learn-card-row">
              <img className="digitalgift-learn-card-arrow" src="/images/digitalgift-workshop-arrow.svg" alt="" aria-hidden="true" />
              <span>איך מזהים עסק עם יתרון תחרותי שמחזיק לאורך שנים</span>
            </div>
            <div className="digitalgift-learn-card-row">
              <img className="digitalgift-learn-card-arrow" src="/images/digitalgift-workshop-arrow.svg" alt="" aria-hidden="true" />
              <span>מתי מניה מעולה היא גם קנייה<span className="digitalgift-mobile-card-copy"><br /></span> טובה, ומתי לא</span>
            </div>
            <div className="digitalgift-learn-card-row">
              <img className="digitalgift-learn-card-arrow" src="/images/digitalgift-workshop-arrow.svg" alt="" aria-hidden="true" />
              <span>תהליך מלא על חברה אמיתית, עם שאלות ותשובות</span>
            </div>
          </div>
        </div>
        <div id="digitalgift-value-cta" style={{ marginTop: 22, minHeight: 300, boxSizing: 'border-box', background: '#05051F', color: '#FFFFFF', border: 'none', display: 'grid', gridTemplateColumns: '0.92fr 1.08fr', gridTemplateRows: '1fr auto', columnGap: 28, padding: 0, direction: 'ltr', overflow: 'hidden', borderRadius: 18 }}>
          <div className="digitalgift-presenter" style={{ gridColumn: 1, gridRow: '1 / -1', minHeight: 300, background: '#D9E1ED', overflow: 'hidden', position: 'relative' }}>
            <img src="/images/digitalgift-alon-figma-lower.jpeg" alt="אלון הזה" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }} />
          </div>
          <div dir="rtl" className="digitalgift-registration-body" style={{ gridColumn: 2, gridRow: 1, alignSelf: 'center', textAlign: 'right', padding: '26px 28px 18px' }}>
            <div className="digitalgift-registration-heading">
              <div>ההרשמה פתוחה</div>
              <div className="digitalgift-registration-title">סדנת השקעות ערך עם אלון הזה</div>
              <div className="digitalgift-registration-attribution"><strong>אלון הזה</strong> · מלמד השקעות ערך מאז 2018</div>
            </div>
            <div className="digitalgift-registration-facts">
              <span><span>עלות</span>ללא עלות</span>
              <span><span>איפה</span>בזום</span>
              <span><span>משך</span>שני ערבים</span>
            </div>
            <div className="digitalgift-registration-action">
              <a href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026" target="_blank" rel="noreferrer" className="digitalgift-workshop-link" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 18, padding: '13px 18px', borderRadius: 999, background: '#F7C948', color: '#05051F', textAlign: 'center', textDecoration: 'none', fontWeight: 800 }}><span style={{ transform: 'translateY(2px)' }}>שמרו לי מקום בסדנה</span></a>
              <div className="digitalgift-registration-note">הסדנה מתקיימת פעם ברבעון · ההרשמה בעמוד הסדנה</div>
            </div>
            <div className="digitalgift-testimonial">
              <img src="/images/digitalgift-testimonial-quote.svg" alt="" aria-hidden="true" />
              <p>[כאן ייכנס ציטוט קצר ממשתתף/ת בסדנה קודמת: מה הם הבינו או עשו אחרת אחרי הסדנה. 2–3 שורות.]</p>
              <div className="digitalgift-testimonial-author">
                <div><b>[שם המשתתף/ת]</b><span>[עיר או תחום · השתתפו בסדנה במחזור …]</span></div>
                <img src="/images/digitalgift-testimonial-avatar.png" alt="" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="digitalgift-mobile-bar" dir="rtl">
        <a href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026" target="_blank" rel="noreferrer">להרשמה</a>
        <span>סדנת השקעות ערך · ללא עלות</span>
      </div>

      {showCookieBanner && (
        <div
          id="digitalgift-cookie-banner"
          style={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 16,
            background: '#FFFFFF',
            border: '1px solid #D1D5DB',
            borderRadius: 12,
            boxShadow: '0 12px 28px rgba(0,0,0,0.14)',
            padding: 14,
            zIndex: 1000,
            maxWidth: 980,
            margin: '0 auto',
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', marginBottom: 8 }}>Cookie Notice</div>
          <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5, marginBottom: 10 }}>
            We use necessary cookies to make our website work properly. With your consent, we may also use functional cookies to improve your experience.
            You can accept or reject non-essential cookies at any time.
          </div>
          <div style={{ fontSize: 12, color: '#4B5563', marginBottom: 12 }}>
            Necessary storage is always enabled for core service operations. Functional storage is optional and used for non-essential improvements.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => applyCookieChoice('accepted')}
              className="digitalgift-primary"
              style={{ border: 'none', background: '#111827', color: '#FFFFFF', borderRadius: 8, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
            >
              Accept
            </button>
            <button
              type="button"
              onClick={() => applyCookieChoice('rejected')}
              style={{ border: '1px solid #D1D5DB', background: '#FFFFFF', color: '#111827', borderRadius: 8, padding: '8px 12px', fontWeight: 700, cursor: 'pointer' }}
            >
              Reject
            </button>
            <button
              type="button"
              onClick={() => openLegalModal('/privacy-policy.html', 'Privacy & Cookies Policy')}
              style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 700, textDecoration: 'underline', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
            >
              Privacy & Cookies Policy
            </button>
          </div>
        </div>
      )}

      {!showCookieBanner && (
        <button
          type="button"
          onClick={() => setShowCookieBanner(true)}
          style={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            border: '1px solid #D1D5DB',
            background: '#FFFFFF',
            color: '#111827',
            borderRadius: 999,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            zIndex: 999,
          }}
        >
          Cookie Settings
        </button>
      )}

      {legalModalUrl && (
        <div
          id="digitalgift-legal-modal"
          role="dialog"
          aria-modal="true"
          aria-label={legalModalTitle || 'Legal document'}
          aria-describedby="legal-modal-description"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.46)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            boxSizing: 'border-box',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLegalModal();
          }}
        >
          <div
            id="digitalgift-legal-modal-panel"
            ref={legalModalPanelRef}
            style={{
              width: 'min(760px, 100%)',
              height: 'min(760px, 92vh)',
              maxHeight: '92%',
              background: '#F3F4F6',
              borderRadius: 18,
              boxShadow: '0 30px 90px rgba(2, 6, 23, 0.42)',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div id="legal-modal-description" style={{ position: 'absolute', width: 1, height: 1, margin: -1, border: 0, padding: 0, clip: 'rect(0 0 0 0)', overflow: 'hidden' }}>
              {legalModalDescription || 'Legal document dialog'}
            </div>
            <div id="legal-modal-header" style={{ padding: '22px 28px 0' }}>
              <h2 style={{ margin: 0, fontFamily: 'Manrope, sans-serif', fontWeight: 700, fontSize: 34, lineHeight: 1, letterSpacing: '0.25px', textAlign: 'center', color: '#0D0D0D' }}>
                GARP Investing Toolbox
              </h2>
              <div style={{ marginTop: 8, fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: 18, lineHeight: 1, textAlign: 'center', color: '#5A5A5A' }}>
                {legalModalTitle || 'Legal document'}
              </div>
            </div>
            <div id="legal-modal-content" style={{ minHeight: 0, flex: 1, padding: '18px 28px 0' }}>
              <iframe
                title={legalModalTitle || 'Legal document'}
                src={legalModalUrl}
                onLoad={styleLegalDocumentScrollbar}
                style={{ width: '100%', height: '100%', minHeight: 220, display: 'block', border: '1px solid #D1D5DB', borderRadius: 6, background: '#FFFFFF' }}
              />
            </div>
            <div id="legal-modal-actions" style={{ padding: '18px 28px 24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <button
                ref={legalModalCloseButtonRef}
                type="button"
                onClick={closeLegalModal}
                style={{ width: 154, height: 52, borderRadius: 26, border: 'none', background: '#0D0D0D', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif', fontWeight: 500, fontSize: 24, lineHeight: 1, cursor: 'pointer' }}
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}

      <footer
        id="digitalgift-footer"
        style={{
          maxWidth: 1184,
          margin: '48px auto 0',
          padding: '72px 0 48px',
          background: 'transparent',
          border: 'none',
          borderTop: '1px solid #E3E8F1',
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 32,
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
        }}
      >
        <div id="digitalgift-value-disclaimer" dir="rtl" style={{ width: '100%', color: '#8A8F98', fontSize: 11, lineHeight: 1.45, textAlign: 'right' }}>
          <span className="digitalgift-desktop-disclaimer">גילוי נאות: המידע בעמוד זה ובסרטון ניתן למטרות לימוד בלבד ואינו מהווה ייעוץ השקעות או המלצה לקנות או למכור ניירות ערך. השקעה בשוק ההון כרוכה בסיכון,<br />לרבות אובדן הקרן. הנתונים עשויים להיות חלקיים או לא מעודכנים. לפני כל החלטת השקעה יש להתייעץ עם בעל רישיון.</span>
          <span className="digitalgift-mobile-disclaimer">גילוי נאות: המידע בעמוד זה ובסרטון ניתן למטרות לימוד בלבד ואינו מהווה ייעוץ השקעות, שיווק השקעות או המלצה לקנות או למכור ניירות ערך. השקעה בשוק ההון כרוכה בסיכון, לרבות אובדן הקרן.</span>
        </div>
        <div id="digitalgift-footer-links" dir="ltr" style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <button type="button" data-a11y-link="true" onClick={() => openLegalModal('/investment-disclaimer.html', 'Investment Disclaimer')} style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 700, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>Investment Disclaimer</button>
          <button type="button" data-a11y-link="true" onClick={() => openLegalModal('/accessibility-statement.html', 'Accessibility Statement')} style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 700, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }} aria-label="Accessibility Statement">Accessibility Statement</button>
          <button type="button" data-a11y-link="true" onClick={() => openLegalModal('/privacy-policy.html', 'Privacy Policy')} style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 700, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>Privacy Policy</button>
          <button type="button" data-a11y-link="true" onClick={() => openLegalModal('/waiting-page-terms.html', 'Terms of Use')} style={{ fontSize: 13, color: '#1D4ED8', fontWeight: 700, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>Terms of Use</button>
        </div>
        <div style={{ fontSize: 12, color: '#6B7280' }}>
          {selectedSymbol && (hasFiscalDataSource || hasFiscalCoverageForSelectedSymbol) ? (
            <>
              Data Source:{' '}
              <a href="https://fiscal.ai/" target="_blank" rel="noreferrer" style={{ color: '#1D4ED8', fontWeight: 700, textDecoration: 'underline' }}>
                Fiscal.ai
              </a>
            </>
          ) : null}
        </div>
      </footer>

      {showQuotaReminder && (
        <div
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShowQuotaReminder(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            boxSizing: 'border-box',
            background: 'rgba(25, 27, 43, 0.62)',
            backdropFilter: 'blur(3px)',
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="quota-reminder-title"
            aria-describedby="quota-reminder-description"
            dir="rtl"
            style={{
              width: 'min(338px, 100%)',
              boxSizing: 'border-box',
              padding: '24px 20px 18px',
              borderRadius: 18,
              background: '#FFFFFF',
              boxShadow: '0 20px 60px rgba(2, 3, 26, 0.24)',
              textAlign: 'center',
              fontFamily: 'Heebo, sans-serif',
            }}
          >
            <div style={{ display: 'table', margin: '0 auto 10px', padding: '4px 12px', borderRadius: 999, background: quotaReminderCount === 0 ? '#FFF0EE' : '#EEF2FF', color: quotaReminderCount === 0 ? '#DC4138' : '#2468FF', fontSize: 12, lineHeight: 1.4, fontWeight: 700 }}>
              {quotaReminderCount === 0 ? '0 חיפושים נותרו' : `נותרו ${quotaReminderCount ?? quotaStatus.remaining} חיפושים`}
            </div>
            <h2 id="quota-reminder-title" style={{ margin: '0 0 10px', color: '#02031A', fontSize: 20, lineHeight: 1.3, fontWeight: 800 }}>
              {quotaReminderCount === 0 ? 'סיימתם את 30 החיפושים החינמיים' : 'רוצים לבחור מניה בעצמכם?'}
            </h2>
            <p id="quota-reminder-description" style={{ margin: '0 0 16px', color: '#73788C', fontSize: 13, lineHeight: 1.45 }}>
              {quotaReminderCount === 0
                ? 'הצעד הבא הוא לדעת לבחור מניות בעצמכם. בסדנה החינמית אלון מלמד את התהליך המלא, שני ערבים בזום.'
                : 'בסדנה החינמית אלון מלמד את התהליך המלא: מה עושים עם מניה שעוברת את 6 הפרמטרים, ואיך מחליטים אם היא שווה השקעה.'}
            </p>
            <a
              href="https://alon-haze-vi.ravpage.co.il/vi-seminar-2026"
              target="_blank"
              rel="noreferrer"
              className="digitalgift-quota-reminder-cta"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 42, boxSizing: 'border-box', borderRadius: 999, background: '#F7C948', color: '#02031A', fontSize: 15, lineHeight: 1.2, fontWeight: 800, textDecoration: 'none' }}
            >
              שמרו לי מקום בסדנה
            </a>
            <button
              ref={quotaReminderCloseButtonRef}
              type="button"
              onClick={() => setShowQuotaReminder(false)}
              style={{ marginTop: 10, padding: '4px 10px', border: 0, background: 'transparent', color: '#73788C', fontFamily: 'inherit', fontSize: 13, lineHeight: 1.4, fontWeight: 700, cursor: 'pointer' }}
            >
              {quotaReminderCount === 0 ? 'אולי אחר כך' : 'להמשיך לחפש'}
            </button>
          </div>
        </div>
      )}

      {showAccessibilityPanel && (
        <div
          id="digitalgift-accessibility-panel"
          ref={accessibilityPanelRef}
          role="dialog"
          aria-label="Accessibility options"
          style={{
            position: 'fixed',
            left: 16,
            bottom: 84,
            width: 392,
            maxWidth: 'calc(100vw - 32px)',
            background: '#FFFFFF',
            border: '1px solid #DBE5F5',
            borderRadius: 16,
            boxShadow: '0 20px 44px rgba(17,24,39,0.20)',
            zIndex: 1201,
            padding: 0,
            overflow: 'hidden',
            animation: a11yReduceMotion ? undefined : 'fadeIn 160ms ease-out',
          }}
        >
          <div style={{ background: 'linear-gradient(135deg, #172554, #2F58F2)', color: '#FFFFFF', padding: '13px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>כלי נגישות</div>
              <div style={{ marginTop: 3, fontSize: 11, opacity: 0.88 }}>התאמות תצוגה וקריאות</div>
            </div>
            <button type="button" onClick={() => setShowAccessibilityPanel(false)} aria-label="Close accessibility options" style={{ border: '1px solid rgba(255,255,255,0.58)', borderRadius: 8, background: 'rgba(255,255,255,0.12)', color: '#FFFFFF', cursor: 'pointer', padding: '5px 9px', fontWeight: 800 }}>×</button>
          </div>

          <div style={{ padding: 12, maxHeight: 'min(78vh, 560px)', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
              <AccessibilityTile icon="A↕" label="הגדלת גופן" active={a11yLargeText} onClick={() => setA11yLargeText((v) => !v)} />
              <AccessibilityTile icon="A" label="גופן קריא" active={a11yReadableFont} onClick={() => setA11yReadableFont((v) => !v)} />
              <AccessibilityTile icon="▧" label="סימון תמונות" active={a11yMarkImages} onClick={() => setA11yMarkImages((v) => !v)} />
              <AccessibilityTile icon="◉" label="הדגשת קישורים" active={a11yUnderlineLinks} onClick={() => setA11yUnderlineLinks((v) => !v)} />
              <AccessibilityTile icon="H" label="הדגשת כותרות" active={a11yHighlightHeadings} onClick={() => setA11yHighlightHeadings((v) => !v)} />
              <AccessibilityTile icon="◫" label="הסתרת תמונות" active={a11yHideImages} onClick={() => setA11yHideImages((v) => !v)} />
              <AccessibilityTile icon="◩" label="היפוך צבעים" active={a11yInvertColors} onClick={() => setA11yInvertColors((v) => !v)} />
              <AccessibilityTile icon="◐" label="שחור לבן" active={a11yGrayscale} onClick={() => setA11yGrayscale((v) => !v)} />
              <AccessibilityTile icon="◒" label="ניגודיות גבוהה" active={a11yHighContrast} onClick={() => setA11yHighContrast((v) => !v)} />
              <AccessibilityTile icon="◌" label="ספיה" active={a11ySepia} onClick={() => setA11ySepia((v) => !v)} />
              <AccessibilityTile icon="◎" label="סמן גדול" active={a11yLargeCursor} onClick={() => setA11yLargeCursor((v) => !v)} />
              <AccessibilityTile icon="✋" label="הקטנת תנועה" active={a11yStopAnimations || a11yReduceMotion} onClick={() => { setA11yStopAnimations((v) => !v); setA11yReduceMotion((v) => !v); }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid #E5E7EB' }}>
              <span style={{ fontSize: 11, color: '#64748B' }}>ההגדרות נשמרות אוטומטית</span>
              <button type="button" onClick={resetAccessibilitySettings} style={{ border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 800 }}>איפוס</button>
            </div>
          </div>
        </div>
      )}

      <button
        ref={accessibilityButtonRef}
        type="button"
        aria-label="Open accessibility options"
        onClick={() => setShowAccessibilityPanel((v) => !v)}
        style={{
          position: 'fixed',
          left: 16,
          bottom: 16,
          width: 56,
          height: 56,
          borderRadius: '50%',
          border: 'none',
          background: '#2F58F2',
          color: '#FFFFFF',
          fontSize: 0,
          lineHeight: 1,
          cursor: 'pointer',
          boxShadow: '0 10px 24px rgba(47,88,242,0.45)',
          outline: showAccessibilityPanel ? '3px solid rgba(47,88,242,0.24)' : 'none',
          zIndex: 1200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="30" height="30" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <circle cx="16" cy="16" r="13.5" fill="#FFFFFF" />
          <circle cx="16" cy="9.2" r="2.1" fill="#2F58F2" />
          <path d="M16 12.8V20.2" stroke="#2F58F2" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M10.6 15.4H21.4" stroke="#2F58F2" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M16 20.2L12.2 25.2" stroke="#2F58F2" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M16 20.2L19.8 25.2" stroke="#2F58F2" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
};

const MetricRow: React.FC<{ label: string; value: string; criterion: string; threshold: number }> = ({ label, value, criterion, threshold }) => {
  const numericValue = Number.parseFloat(value.replace(/[^0-9.+-]/g, ''));
  const hasValue = Number.isFinite(numericValue);
  const passes = hasValue && numericValue >= threshold;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 88px 24px', alignItems: 'center', columnGap: 7, rowGap: 4, borderBottom: '1px solid #B8C8E2', padding: '8px 32px 8px 0', textAlign: 'left' }}>
      <span style={{ paddingLeft: 48, fontWeight: 700, color: '#111827', fontSize: 14, textAlign: 'left' }}>{label}</span>
      <span style={{ fontWeight: 700, color: '#1F2937', fontSize: 14, whiteSpace: 'nowrap', textAlign: 'right' }}>{value}</span>
      <span aria-label={hasValue ? (passes ? 'Meets criterion' : 'Does not meet criterion') : undefined} style={{ color: passes ? '#16A34A' : '#DC2626', fontSize: 18, fontWeight: 900, lineHeight: 1, textAlign: 'center' }}>{hasValue ? (passes ? '✓' : '✕') : null}</span>
      <span style={{ gridColumn: '1 / -1', color: '#8A8F98', fontSize: 11, textAlign: 'center' }}>{criterion}</span>
    </div>
  );
};

const AccessibilityTile: React.FC<{
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
}> = ({ icon, label, active, onClick }) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    style={{
      minHeight: 76,
      border: active ? '2px solid #2F58F2' : '1px solid #D9E1EE',
      borderRadius: 10,
      background: active ? '#EEF3FF' : '#FFFFFF',
      color: '#111827',
      cursor: 'pointer',
      padding: '8px 5px 7px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 5,
      boxShadow: active ? '0 3px 10px rgba(47,88,242,0.14)' : '0 1px 2px rgba(15,23,42,0.04)',
      transition: 'border-color 140ms ease, background 140ms ease, box-shadow 140ms ease',
    }}
  >
    <span aria-hidden="true" style={{ height: 26, display: 'flex', alignItems: 'center', fontSize: 22, fontWeight: 800, lineHeight: 1 }}>{icon}</span>
    <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.15, textAlign: 'center' }}>{label}</span>
    <span style={{ fontSize: 9, color: active ? '#2F58F2' : '#94A3B8', fontWeight: 800 }}>{active ? 'פעיל' : ''}</span>
  </button>
);

export default DigitalGiftScreen;
